import * as React from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import {
  BufferGeometry,
  CanvasTexture,
  CatmullRomCurve3,
  DoubleSide,
  Group,
  LinearFilter,
  Vector3,
} from "three";
import { createPortal } from "react-dom";

import {
  pointToThree,
  playerColor,
  type ShotUniverseContext,
  type ShotUniverseOption,
  type ShotUniverseShotFamily,
} from "./shotUniverseHelpers";
import type { CvJson } from "../../shared/types/cvJson";

type CameraView = {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
  roll: number;
  offset: [number, number];
};

type ShapeFanVariant = {
  label: string;
  color: string;
  trajectory: Vector3[];
  peakHeightFt: number;
  estimatedArrivalSec: number;
  launchAngleDeg: number;
  initialSpeedMph: number;
  averageSpeedMph: number | null;
  spinRpm: number;
  netClearanceFt: number | null;
  fitErrorFt?: number | null;
  isCustomPath?: boolean;
  isActualPath?: boolean;
  stoppedAtNet?: boolean;
  missesTarget?: boolean;
  cannotHitTarget?: boolean;
};

class ShotUniverseErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

export type ShotUniverseLabTarget = {
  x: number;
  y: number;
  z: number;
  label?: string | null;
  pathLabel?: string | null;
};

export type ShotUniverseLabSelection = {
  target: ShotUniverseLabTarget | null;
  targets?: ShotUniverseLabTarget[];
  pathLabel: string | null;
};

export type ShotUniverseLabSelectionRestore = {
  nonce: number;
  target?: { x: number; y: number; z: number } | null;
  targets?: Array<{ x: number; y: number; z: number; label?: string | null }> | null;
  pathLabel?: string | null;
};

function ShotUniverseWebGlFallback() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        boxSizing: "border-box",
        color: "rgba(255,255,255,0.82)",
        background: "rgba(0,0,0,0.78)",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: 13,
        textAlign: "center",
        lineHeight: 1.35,
      }}
    >
      The 3D Target Lab could not get a WebGL context. Close other video or 3D tabs,
      then reopen the lab.
    </div>
  );
}

type CustomShapeFanControl = {
  mode: "rpm" | "angle" | "mph";
  rpm: number;
  angle: number;
  mph: number;
};

type ShapeFanVariantPick = {
  label: string;
  color: string;
  targetNetClearanceFt?: number;
  maxNetClearanceFt?: number;
  minPeakHeightFt?: number;
  preferLob?: boolean;
  preferMaxSpeedClosestNet?: boolean;
  preferSlowClosestNet?: boolean;
  speedTieToleranceMph?: number;
  allowSimilar?: boolean;
};

type CustomShapeFanGeometry = {
  start: Vector3;
  target: Vector3;
};

type SelectedTargetSummary = {
  distanceFt: number | null;
};

type PathReactionEstimate = {
  playerId: number;
  timeSec: number;
  lateralDistanceFt: number;
  ballHeightFt: number;
};

type PhysicsFitSummary = {
  distanceFt: number | null;
};

type ActualLandingExtrapolation = {
  trajectory: Vector3[];
  landing: Vector3;
  uncertaintyRadiusFt: number;
  fitErrorFt: number | null;
};

export type ShotUniverseVideoCourtProjection = {
  outer: string;
  lines: string[];
};

const NET_MIDPOINT_FLOOR = pointToThree({ x: 10, y: 22, z: 0 });
const NET_MIDPOINT_FLOOR_ARRAY: [number, number, number] = [
  NET_MIDPOINT_FLOOR.x,
  NET_MIDPOINT_FLOOR.y,
  NET_MIDPOINT_FLOOR.z,
];
const NEGATIVE_NET_MIDPOINT_FLOOR_ARRAY: [number, number, number] = [
  -NET_MIDPOINT_FLOOR.x,
  -NET_MIDPOINT_FLOOR.y,
  -NET_MIDPOINT_FLOOR.z,
];
const ROTATION_STEP_RADIANS = Math.PI / 12;

export type ShotUniverseCameraAdjust = {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
  roll?: number;
  offset?: [number, number];
};

export type ShotUniverseVisibility = {
  actualOpacity: number;
  actualDimOpacity: number;
  actualTubeRadius: number;
  ghostOpacity: number;
  ghostBadOpacity: number;
  ghostSelectedOpacity: number;
  ghostDimOpacity: number;
  shapeFanOpacityBase: number;
  shapeFanOpacityStep: number;
  targetOpacity: number;
  targetDimOpacity: number;
};

const DEFAULT_VISIBILITY: ShotUniverseVisibility = {
  actualOpacity: 0.96,
  actualDimOpacity: 0.22,
  actualTubeRadius: 0.08,
  ghostOpacity: 0.34,
  ghostBadOpacity: 0.18,
  ghostSelectedOpacity: 0.92,
  ghostDimOpacity: 0.08,
  shapeFanOpacityBase: 0.52,
  shapeFanOpacityStep: 0.055,
  targetOpacity: 0.84,
  targetDimOpacity: 0.2,
};

function clampOpacity(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function resolveVisibility(
  visibility: Partial<ShotUniverseVisibility> | undefined,
): ShotUniverseVisibility {
  return {
    ...DEFAULT_VISIBILITY,
    ...visibility,
  };
}

function MaybePortal(props: {
  container?: HTMLElement | null;
  children: React.ReactNode;
}) {
  return props.container ? createPortal(props.children, props.container) : <>{props.children}</>;
}

function TargetLabelSprite(props: {
  label: string;
  color: string;
  background: string;
  position: [number, number, number];
  scale?: [number, number, number];
  renderOrder?: number;
}) {
  const texture = React.useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 144;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = props.background;
      ctx.strokeStyle = props.color;
      ctx.lineWidth = 4;
      const radius = 28;
      const x = 8;
      const y = 8;
      const width = canvas.width - 16;
      const height = canvas.height - 16;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      const fontSize = props.label.length > 9 ? 50 : 60;
      ctx.font = `900 ${fontSize}px ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(props.label, canvas.width / 2, canvas.height / 2 + 1);
    }
    const nextTexture = new CanvasTexture(canvas);
    nextTexture.minFilter = LinearFilter;
    nextTexture.magFilter = LinearFilter;
    nextTexture.needsUpdate = true;
    return nextTexture;
  }, [props.background, props.color, props.label]);

  React.useEffect(() => () => texture.dispose(), [texture]);

  return (
    <sprite
      position={props.position}
      scale={props.scale ?? [6.2, 1.75, 1]}
      renderOrder={props.renderOrder ?? 120}
    >
      <spriteMaterial
        map={texture}
        transparent
        depthTest={false}
        depthWrite={false}
      />
    </sprite>
  );
}

function LinePoints(props: {
  points: Vector3[];
  color: string;
  opacity?: number;
  lineWidth?: number;
  onClick?: (event: any) => void;
  onPointerOver?: (event: any) => void;
  onPointerOut?: (event: any) => void;
}) {
  const geometry = React.useMemo(
    () => new BufferGeometry().setFromPoints(props.points),
    [props.points],
  );

  React.useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <line
      onClick={props.onClick}
      onPointerOver={props.onPointerOver}
      onPointerOut={props.onPointerOut}
    >
      <primitive object={geometry} attach="geometry" />
      <lineBasicMaterial
        color={props.color}
        transparent
        opacity={props.opacity ?? 1}
        linewidth={props.lineWidth ?? 1}
      />
    </line>
  );
}

function TrajectoryTube(props: {
  points: Vector3[];
  color: string;
  opacity?: number;
  radius?: number;
  roundedEnds?: boolean;
  depthTest?: boolean;
  wireframe?: boolean;
  radialSegments?: number;
  tubularSegmentsMultiplier?: number;
}) {
  const curve = React.useMemo(
    () => new CatmullRomCurve3(props.points, false, "catmullrom", 0.5),
    [props.points],
  );
  const tubularSegments = Math.max(
    8,
    Math.round(props.points.length * (props.tubularSegmentsMultiplier ?? 3)),
  );
  const radius = props.radius ?? 0.08;
  const radialSegments = props.radialSegments ?? 10;
  const startPoint = props.points[0] ?? null;
  const endPoint = props.points[props.points.length - 1] ?? null;
  const startPosition = startPoint
    ? ([startPoint.x, startPoint.y, startPoint.z] as [number, number, number])
    : null;
  const endPosition = endPoint
    ? ([endPoint.x, endPoint.y, endPoint.z] as [number, number, number])
    : null;

  return (
    <group>
      <mesh>
        <tubeGeometry args={[curve, tubularSegments, radius, radialSegments, false]} />
        <meshBasicMaterial
          color={props.color}
          transparent
          opacity={props.opacity ?? 1}
          depthTest={props.depthTest ?? false}
          wireframe={props.wireframe}
        />
      </mesh>
      {props.roundedEnds && startPosition ? (
        <mesh position={startPosition}>
          <sphereGeometry args={[radius, 12, 8]} />
          <meshBasicMaterial
            color={props.color}
            transparent
            opacity={props.opacity ?? 1}
            depthTest={props.depthTest ?? false}
          />
        </mesh>
      ) : null}
      {props.roundedEnds && endPosition ? (
        <mesh position={endPosition}>
          <sphereGeometry args={[radius, 12, 8]} />
          <meshBasicMaterial
            color={props.color}
            transparent
            opacity={props.opacity ?? 1}
            depthTest={props.depthTest ?? false}
          />
        </mesh>
      ) : null}
    </group>
  );
}

function AnimatedTrajectoryBall(props: {
  points: Vector3[];
  color: string;
  durationSec: number;
  opacity?: number;
  radius?: number;
  innerRadius?: number;
}) {
  const groupRef = React.useRef<Group | null>(null);
  const animationStartRef = React.useRef<number | null>(null);
  const restartTimerRef = React.useRef<number | null>(null);
  const curve = React.useMemo(
    () => new CatmullRomCurve3(props.points, false, "catmullrom", 0.5),
    [props.points],
  );
  const durationSec = Math.max(0.25, props.durationSec);

  React.useEffect(() => {
    if (restartTimerRef.current != null) {
      window.clearTimeout(restartTimerRef.current);
    }
    restartTimerRef.current = window.setTimeout(() => {
      animationStartRef.current = null;
      restartTimerRef.current = null;
    }, 160);
    return () => {
      if (restartTimerRef.current != null) {
        window.clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
    };
  }, [props.points, props.durationSec]);

  useFrame(({ clock }) => {
    const group = groupRef.current;
    if (!group || props.points.length < 2) return;
    if (animationStartRef.current == null) {
      animationStartRef.current = clock.getElapsedTime();
    }
    const elapsed = Math.max(0, clock.getElapsedTime() - animationStartRef.current);
    const t = (elapsed % durationSec) / durationSec;
    group.position.copy(curve.getPoint(t));
  });

  if (props.points.length < 2) return null;

  return (
    <group ref={groupRef}>
      <mesh renderOrder={50}>
        <sphereGeometry args={[props.radius ?? 0.26, 18, 14]} />
        <meshBasicMaterial
          color={props.color}
          transparent
          opacity={props.opacity ?? 0.98}
          depthTest={false}
        />
      </mesh>
      <mesh renderOrder={51}>
        <sphereGeometry args={[props.innerRadius ?? 0.12, 14, 10]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={Math.min(0.82, props.opacity ?? 0.82)}
          depthTest={false}
        />
      </mesh>
    </group>
  );
}

function ExtrapolationCone(props: {
  extrapolation: ActualLandingExtrapolation;
  dimmed: boolean;
}) {
  const trajectory = props.extrapolation.trajectory;
  const segments = React.useMemo(() => {
    const segmentCount = 7;
    const out: Array<{ points: Vector3[]; radius: number; opacity: number }> = [];
    for (let i = 0; i < segmentCount; i += 1) {
      const startIndex = Math.max(
        0,
        Math.floor(((trajectory.length - 1) * i) / segmentCount) - (i > 0 ? 1 : 0),
      );
      const endIndex = Math.min(
        trajectory.length - 1,
        Math.ceil(((trajectory.length - 1) * (i + 1)) / segmentCount) + 1,
      );
      const points = trajectory.slice(startIndex, endIndex + 1);
      if (points.length < 2) continue;
      const t = (i + 1) / segmentCount;
      out.push({
        points,
        radius: 0.04 + props.extrapolation.uncertaintyRadiusFt * 0.13 * t,
        opacity: (props.dimmed ? 0.08 : 0.16) * (0.65 + t * 0.35),
      });
    }
    return out;
  }, [props.dimmed, props.extrapolation.uncertaintyRadiusFt, trajectory]);

  return (
    <group>
      <LinePoints
        points={trajectory}
        color="#ffffff"
        opacity={props.dimmed ? 0.18 : 0.36}
        lineWidth={1}
      />
      {segments.map((segment, index) => (
        <TrajectoryTube
          key={`extrapolation-cone-${index}`}
          points={segment.points}
          color="#ffffff"
          opacity={clampOpacity(segment.opacity)}
          radius={segment.radius}
          depthTest={false}
          radialSegments={7}
          tubularSegmentsMultiplier={1.4}
        />
      ))}
    </group>
  );
}

function CourtLineVisual(props: {
  points: Vector3[];
  color: string;
  opacity?: number;
  lineWidth?: number;
  videoAligned?: boolean;
}) {
  if (props.videoAligned) {
    return (
      <group>
        <TrajectoryTube
          points={props.points}
          color={props.color}
          opacity={(props.opacity ?? 1) * 0.34}
          radius={0.06}
          depthTest={false}
          radialSegments={6}
          tubularSegmentsMultiplier={1.2}
        />
        <LinePoints
          points={props.points}
          color="#ff2b2b"
          opacity={1}
          lineWidth={1}
        />
      </group>
    );
  }
  return (
    <LinePoints
      points={props.points}
      color={props.color}
      opacity={props.opacity}
      lineWidth={props.lineWidth}
    />
  );
}

function CourtLines(props: { videoAligned?: boolean }) {
  const videoAligned = Boolean(props.videoAligned);
  const lineColor = videoAligned ? "#ff2b2b" : "#ffffff";
  const nearOpacity = videoAligned ? 0.98 : 0.82;
  const farOpacity = videoAligned ? 0.72 : 0.62;
  const centerOpacity = videoAligned ? 0.78 : 0.42;
  const lineWidth = videoAligned ? 3 : 1;
  return (
    <group>
      <CourtLineVisual
        points={[new Vector3(-10, 0, 22), new Vector3(10, 0, 22)]}
        color={lineColor}
        opacity={nearOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(-10, 0, 0), new Vector3(-10, 0, 22)]}
        color={lineColor}
        opacity={nearOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(10, 0, 0), new Vector3(10, 0, 22)]}
        color={lineColor}
        opacity={nearOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[
          new Vector3(-10, 0, -22),
          new Vector3(10, 0, -22),
        ]}
        color={lineColor}
        opacity={farOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[
          new Vector3(-10, 0, -22),
          new Vector3(-10, 0, 0),
        ]}
        color={lineColor}
        opacity={farOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[
          new Vector3(10, 0, -22),
          new Vector3(10, 0, 0),
        ]}
        color={lineColor}
        opacity={farOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(-10, 0, 0), new Vector3(10, 0, 0)]}
        color={lineColor}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(-10, 0, -7), new Vector3(10, 0, -7)]}
        color={lineColor}
        opacity={farOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(-10, 0, 7), new Vector3(10, 0, 7)]}
        color={lineColor}
        opacity={nearOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(0, 0, -22), new Vector3(0, 0, -7)]}
        color={lineColor}
        opacity={centerOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      <CourtLineVisual
        points={[new Vector3(0, 0, 7), new Vector3(0, 0, 22)]}
        color={lineColor}
        opacity={centerOpacity}
        lineWidth={lineWidth}
        videoAligned={videoAligned}
      />
      {videoAligned ? null : (
        <LinePoints
          points={[
            new Vector3(-10, 0, 0),
            new Vector3(-10, 3, 0),
            new Vector3(10, 3, 0),
            new Vector3(10, 0, 0),
          ]}
          color={lineColor}
          opacity={0.55}
        />
      )}
    </group>
  );
}

function optionColor(option: ShotUniverseOption): string {
  if (option.heuristicLabel === "aggressive") return "#ffcf4a";
  if (option.heuristicLabel === "safeNeutral") return "#8ee68e";
  if (option.heuristicLabel === "bad") return "#ff6b6b";
  return "#d9d9d9";
}

function referenceLabelForIndex(index: number): string {
  return index >= 0 && index < 26
    ? `Reference ${String.fromCharCode(65 + index)}`
    : `Reference ${index + 1}`;
}

function shortReferenceLabelForIndex(index: number): string {
  return index >= 0 && index < 26 ? `Ref ${String.fromCharCode(65 + index)}` : `Ref ${index + 1}`;
}

function formatHeuristicLabel(label: ShotUniverseOption["heuristicLabel"]): string {
  if (label === "aggressive") return "Aggressive";
  if (label === "safeNeutral") return "Safe / neutral";
  if (label === "bad") return "Low value";
  return "Unscored";
}

function formatTargetDistance(option: ShotUniverseOption): string {
  const start = option.trajectory[0] ?? null;
  if (!start) return "distance n/a";
  const distanceFt = Math.hypot(option.target.x - start.x, option.target.z - start.z);
  return `${distanceFt.toFixed(1)} ft`;
}

function countUniqueTargetChoices(options: ShotUniverseOption[]): number {
  const targets: ShotUniverseOption[] = [];
  for (const option of options) {
    const alreadyCounted = targets.some(
      (target) => Math.hypot(target.target.x - option.target.x, target.target.z - option.target.z) < 0.2,
    );
    if (!alreadyCounted) targets.push(option);
  }
  return targets.length;
}

function shapeFanVariantIdentity(variant: ShapeFanVariant): string {
  if (variant.isActualPath) return "actual";
  if (variant.isCustomPath) return "custom";
  return variant.label;
}

function shapeFanLabelMatchesVariant(
  label: string | null,
  variant: ShapeFanVariant,
): boolean {
  if (!label) return false;
  if (variant.isActualPath) return label === "Actual shot";
  if (variant.isCustomPath) return label.startsWith("Custom");
  return label === variant.label;
}

function makeNumberRange(start: number, end: number, step: number): number[] {
  const out: number[] = [];
  for (let value = start; value <= end; value += step) {
    out.push(value);
  }
  return out;
}

const PICKLEBALL_YELLOW = "#d7e957";
const PRACTICE_TARGET_ORANGE = "#ff7a1a";
const CUSTOM_PATH_COLOR = "#ff8bd8";
const SHOW_ACTUAL_FIT_EXPERIMENT = false;
const SHOW_LOB_SHAPE_FAN_VARIANT = false;
const EXTEND_SHAPE_FAN_AFTER_BOUNCE = false;
const COURT_ORBIT_TARGET = new Vector3(0, 1.45, 0);
const COURT_ORBIT_FALLBACK_OFFSET = new Vector3(0, 5.5, 42);
const COURT_ORBIT_FOV = 62;
const COURT_ORBIT_MIN_DISTANCE = 22;
const COURT_ORBIT_MAX_DISTANCE = 58;
const PHYSICS_PATH_COLORS = ["#7fd7ff", "#8ee68e", "#ffcf4a", "#d9d9d9", "#c5a3ff"];
const USE_DENSE_PHYSICS_SEARCH = true;
const COARSE_TOPSPIN_RPM_BUCKETS = [900, 1700, 2500];
const DENSE_TOPSPIN_RPM_BUCKETS = [
  600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800,
  1900, 2000, 2100, 2200, 2300, 2400, 2500, 2600,
];
const COARSE_SOLVER_ANGLE_DEGREES = [3, 6, 9, 12, 16, 20, 25, 30, 36, 42, 48];
const DENSE_SOLVER_ANGLE_DEGREES = makeNumberRange(1, 70, 1);
const TOPSPIN_RPM_BUCKETS = USE_DENSE_PHYSICS_SEARCH
  ? DENSE_TOPSPIN_RPM_BUCKETS
  : COARSE_TOPSPIN_RPM_BUCKETS;
const SOLVER_ANGLE_DEGREES = USE_DENSE_PHYSICS_SEARCH
  ? DENSE_SOLVER_ANGLE_DEGREES
  : COARSE_SOLVER_ANGLE_DEGREES;
const DEFAULT_SELECTED_TARGET_PANEL_FRAME = {
  right: 16,
  top: 92,
};
const FT_TO_M = 0.3048;
const M_TO_FT = 1 / FT_TO_M;
const MPH_TO_MPS = 0.44704;
const PICKLEBALL_DIAMETER_M = 0.074;
const PICKLEBALL_MASS_KG = 0.026;
const AIR_DENSITY_KG_M3 = 1.225;
const PICKLEBALL_DRAG_COEFFICIENT = 0.5;
const GRAVITY_MPS2 = 9.80665;
const NET_HEIGHT_FT = 3;
const MIN_SOLVED_NET_CLEARANCE_FT = 0.25;
const SOLVER_MIN_SPEED_MPH = 6;
const SOLVER_MAX_SPEED_MPH = 95;
const SOLVER_SPEED_SCAN_STEP_MPH = 3;
const CUSTOM_SHAPE_FAN_DEFAULT: CustomShapeFanControl = {
  mode: "rpm",
  rpm: 0,
  angle: 20,
  mph: 24,
};
const CUSTOM_SPIN_RPM_BUCKETS = [
  -1600, -1400, -1200, -1000, -800, -600, -300, 0, 600, 900, 1200, 1500,
  1800, 2100, 2400, 2600,
];

function magnusLiftCoefficient(spinRpm: number): number {
  const absSpinRpm = Math.abs(spinRpm);
  if (absSpinRpm <= 0) return 0;
  return clampNumber(absSpinRpm * 0.00008, 0.05, 0.24);
}

function simulateTopspinTrajectory(args: {
  start: Vector3;
  target: Vector3;
  direction: Vector3;
  distanceFt: number;
  launchAngleDeg: number;
  speedMph: number;
  spinRpm: number;
}): {
  points: Vector3[];
  yAtTargetFt: number | null;
  flightSec: number | null;
  peakHeightFt: number;
  netClearanceFt: number | null;
} {
  const areaM2 = Math.PI * (PICKLEBALL_DIAMETER_M / 2) ** 2;
  const dragK =
    (0.5 * AIR_DENSITY_KG_M3 * PICKLEBALL_DRAG_COEFFICIENT * areaM2) /
    PICKLEBALL_MASS_KG;
  const magnusK =
    (0.5 * AIR_DENSITY_KG_M3 * magnusLiftCoefficient(args.spinRpm) * areaM2) /
    PICKLEBALL_MASS_KG;
  const targetDistanceM = args.distanceFt * FT_TO_M;
  const angleRad = (args.launchAngleDeg * Math.PI) / 180;
  const speedMps = args.speedMph * MPH_TO_MPS;
  let sM = 0;
  let yM = args.start.y * FT_TO_M;
  let vsMps = speedMps * Math.cos(angleRad);
  let vyMps = speedMps * Math.sin(angleRad);
  let timeSec = 0;
  let peakHeightFt = args.start.y;
  let netClearanceFt: number | null = null;
  const out: Vector3[] = [args.start.clone()];
  const dt = 0.008;
  let previousSM = sM;
  let previousYM = yM;

  for (let i = 0; i < 900; i += 1) {
    const speed = Math.hypot(vsMps, vyMps);
    const dragAs = speed > 0 ? -dragK * speed * vsMps : 0;
    const dragAy = speed > 0 ? -dragK * speed * vyMps : 0;
    const magnusAy =
      args.spinRpm === 0 ? 0 : (args.spinRpm > 0 ? -1 : 1) * magnusK * speed * speed;
    const ay = dragAy + magnusAy - GRAVITY_MPS2;

    vsMps += dragAs * dt;
    vyMps += ay * dt;
    previousSM = sM;
    previousYM = yM;
    sM += Math.max(0, vsMps * dt);
    yM += vyMps * dt;
    timeSec += dt;

    const point = args.start
      .clone()
      .add(args.direction.clone().multiplyScalar(sM * M_TO_FT));
    point.y = yM * M_TO_FT;
    out.push(point);
    peakHeightFt = Math.max(peakHeightFt, point.y);

    if (netClearanceFt == null) {
      const previousZ = args.start.z + args.direction.z * previousSM * M_TO_FT;
      const currentZ = point.z;
      if ((previousZ <= 0 && currentZ >= 0) || (previousZ >= 0 && currentZ <= 0)) {
        const span = currentZ - previousZ;
        const t = Math.abs(span) < 0.001 ? 0 : (0 - previousZ) / span;
        const netHeightFt = (previousYM + (yM - previousYM) * t) * M_TO_FT;
        netClearanceFt = netHeightFt - NET_HEIGHT_FT;
      }
    }

    if (sM >= targetDistanceM) {
      const span = sM - previousSM;
      const t = span > 0 ? (targetDistanceM - previousSM) / span : 0;
      const yAtTargetFt = (previousYM + (yM - previousYM) * t) * M_TO_FT;
      const flightSec = timeSec - dt + dt * t;
      const last = args.target.clone();
      last.y = yAtTargetFt;
      out[out.length - 1] = last;
      return { points: out, yAtTargetFt, flightSec, peakHeightFt, netClearanceFt };
    }

    if (yM * M_TO_FT < -1 || timeSec > 4.5) {
      break;
    }
  }

  return { points: out, yAtTargetFt: null, flightSec: null, peakHeightFt, netClearanceFt };
}

function simulateTopspinTrajectoryToGround(args: {
  start: Vector3;
  direction: Vector3;
  launchAngleDeg: number;
  speedMph: number;
  spinRpm: number;
}): Vector3[] {
  const areaM2 = Math.PI * (PICKLEBALL_DIAMETER_M / 2) ** 2;
  const dragK =
    (0.5 * AIR_DENSITY_KG_M3 * PICKLEBALL_DRAG_COEFFICIENT * areaM2) /
    PICKLEBALL_MASS_KG;
  const magnusK =
    (0.5 * AIR_DENSITY_KG_M3 * magnusLiftCoefficient(args.spinRpm) * areaM2) /
    PICKLEBALL_MASS_KG;
  const angleRad = (args.launchAngleDeg * Math.PI) / 180;
  const speedMps = args.speedMph * MPH_TO_MPS;
  let sM = 0;
  let yM = args.start.y * FT_TO_M;
  let vsMps = speedMps * Math.cos(angleRad);
  let vyMps = speedMps * Math.sin(angleRad);
  let timeSec = 0;
  const out: Vector3[] = [args.start.clone()];
  const dt = 0.008;
  let previousSM = sM;
  let previousYM = yM;

  for (let i = 0; i < 900; i += 1) {
    const speed = Math.hypot(vsMps, vyMps);
    const dragAs = speed > 0 ? -dragK * speed * vsMps : 0;
    const dragAy = speed > 0 ? -dragK * speed * vyMps : 0;
    const magnusAy =
      args.spinRpm === 0 ? 0 : (args.spinRpm > 0 ? -1 : 1) * magnusK * speed * speed;
    const ay = dragAy + magnusAy - GRAVITY_MPS2;

    vsMps += dragAs * dt;
    vyMps += ay * dt;
    previousSM = sM;
    previousYM = yM;
    sM += Math.max(0, vsMps * dt);
    yM += vyMps * dt;
    timeSec += dt;

    const point = args.start
      .clone()
      .add(args.direction.clone().multiplyScalar(sM * M_TO_FT));
    point.y = yM * M_TO_FT;
    out.push(point);

    if (point.y <= 0 && timeSec > 0.05) {
      const span = yM - previousYM;
      const t = Math.abs(span) < 0.001 ? 1 : clampNumber((0 - previousYM) / span, 0, 1);
      const landingSM = previousSM + (sM - previousSM) * t;
      const landing = args.start
        .clone()
        .add(args.direction.clone().multiplyScalar(landingSM * M_TO_FT));
      landing.y = 0;
      out[out.length - 1] = landing;
      break;
    }

    if (timeSec > 4.5 || Math.abs(point.z) > 45 || Math.abs(point.x) > 35) {
      break;
    }
  }

  return out;
}

function cumulativeHorizontalDistances(points: Vector3[]): number[] {
  const distances = [0];
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    distances.push(
      distances[i - 1] + Math.hypot(current.x - previous.x, current.z - previous.z),
    );
  }
  return distances;
}

function orientTrajectoryFromContact(points: Vector3[], contactPoint?: Vector3 | null): Vector3[] {
  if (points.length < 2 || !contactPoint) return points;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const firstDistance = Math.hypot(first.x - contactPoint.x, first.z - contactPoint.z);
  const lastDistance = Math.hypot(last.x - contactPoint.x, last.z - contactPoint.z);
  return lastDistance + 0.35 < firstDistance ? points.slice().reverse() : points;
}

function sampleTrajectoryAtFraction(
  points: Vector3[],
  distances: number[],
  fraction: number,
): Vector3 | null {
  if (!points.length || distances.length !== points.length) return null;
  const total = distances[distances.length - 1] ?? 0;
  if (total <= 0) return points[0]?.clone() ?? null;
  const targetDistance = clampNumber(fraction, 0, 1) * total;
  for (let i = 1; i < distances.length; i += 1) {
    if (distances[i] < targetDistance) continue;
    const previousDistance = distances[i - 1];
    const span = distances[i] - previousDistance;
    const t = span > 0 ? (targetDistance - previousDistance) / span : 0;
    return points[i - 1].clone().lerp(points[i], t);
  }
  return points[points.length - 1]?.clone() ?? null;
}

function sampleTrajectoryAtDistance(
  points: Vector3[],
  distances: number[],
  targetDistance: number,
): Vector3 | null {
  if (!points.length || distances.length !== points.length) return null;
  if (targetDistance <= 0) return points[0]?.clone() ?? null;
  for (let i = 1; i < distances.length; i += 1) {
    if (distances[i] < targetDistance) continue;
    const previousDistance = distances[i - 1];
    const span = distances[i] - previousDistance;
    const t = span > 0 ? (targetDistance - previousDistance) / span : 0;
    return points[i - 1].clone().lerp(points[i], t);
  }
  return points[points.length - 1]?.clone() ?? null;
}

function trajectoryFitErrorFt(candidate: Vector3[], actual: Vector3[]): number | null {
  if (candidate.length < 2 || actual.length < 2) return null;
  const candidateDistances = cumulativeHorizontalDistances(candidate);
  const actualDistances = cumulativeHorizontalDistances(actual);
  const sampleCount = 101;
  let sumSq = 0;
  let count = 0;
  for (let i = 0; i < sampleCount; i += 1) {
    const fraction = i / (sampleCount - 1);
    const candidatePoint = sampleTrajectoryAtFraction(
      candidate,
      candidateDistances,
      fraction,
    );
    const actualPoint = sampleTrajectoryAtFraction(actual, actualDistances, fraction);
    if (!candidatePoint || !actualPoint) continue;
    const dx = candidatePoint.x - actualPoint.x;
    const dy = candidatePoint.y - actualPoint.y;
    const dz = candidatePoint.z - actualPoint.z;
    sumSq += dx * dx + dy * dy + dz * dz;
    count += 1;
  }
  return count > 0 ? Math.sqrt(sumSq / count) : null;
}

function buildPhysicsFitVariants(args: {
  start: Vector3 | null;
  target: Vector3 | null;
  labels?: string[];
  colors?: string[];
  maxCount?: number;
  variantPicks?: ShapeFanVariantPick[];
  actualTrajectory?: Vector3[];
  sortByFitError?: boolean;
  spinRpmBuckets?: number[];
  launchAngleDegrees?: number[];
  fixedSpeedMph?: number;
  allowNetHit?: boolean;
}): ShapeFanVariant[] {
  const { start, target } = args;
  if (!start || !target) return [];
  const distance = Math.hypot(target.x - start.x, target.z - start.z);
  if (distance <= 0.5) return [];
  const direction = new Vector3(target.x - start.x, 0, target.z - start.z).normalize();
  const shouldCrossNet = start.z === 0 || target.z === 0 || start.z * target.z < 0;
  const solved: ShapeFanVariant[] = [];

  const spinRpmBuckets = args.spinRpmBuckets ?? TOPSPIN_RPM_BUCKETS;
  const launchAngleDegrees = args.launchAngleDegrees ?? SOLVER_ANGLE_DEGREES;

  for (const spinRpm of spinRpmBuckets) {
    const evaluateAt = (launchAngleDeg: number, speedMph: number) =>
        simulateTopspinTrajectory({
          start,
          target,
          direction,
          distanceFt: distance,
          launchAngleDeg,
          speedMph,
          spinRpm,
        });

    if (args.fixedSpeedMph != null) {
      const speedSamples: Array<{
        launchAngleDeg: number;
        missFt: number;
      }> = [];
      for (const launchAngleDeg of launchAngleDegrees) {
        const result = evaluateAt(launchAngleDeg, args.fixedSpeedMph);
        if (result.yAtTargetFt == null) continue;
        speedSamples.push({
          launchAngleDeg,
          missFt: result.yAtTargetFt - target.y,
        });
      }

      for (let sampleIndex = 1; sampleIndex < speedSamples.length; sampleIndex += 1) {
        const previousSample = speedSamples[sampleIndex - 1];
        const currentSample = speedSamples[sampleIndex];
        if (!previousSample || !currentSample) continue;
        if (previousSample.missFt * currentSample.missFt > 0) continue;

        let lo = previousSample.launchAngleDeg;
        let hi = currentSample.launchAngleDeg;
        let loMissFt = previousSample.missFt;
        let best = evaluateAt(hi, args.fixedSpeedMph);
        let bestLaunchAngleDeg = hi;
        for (let i = 0; i < 20; i += 1) {
          const mid = (lo + hi) / 2;
          const midResult = evaluateAt(mid, args.fixedSpeedMph);
          if (midResult.yAtTargetFt == null) break;
          const midMissFt = midResult.yAtTargetFt - target.y;
          best = midResult;
          bestLaunchAngleDeg = mid;
          if (loMissFt * midMissFt <= 0) {
            hi = mid;
          } else {
            lo = mid;
            loMissFt = midMissFt;
          }
        }

        if (best.flightSec == null || best.points.length < 3) continue;
        if (
          !args.allowNetHit &&
          shouldCrossNet &&
          (best.netClearanceFt == null ||
            best.netClearanceFt <= MIN_SOLVED_NET_CLEARANCE_FT)
        ) {
          continue;
        }
        solved.push({
          label: "Path",
          color: PHYSICS_PATH_COLORS[0],
          trajectory: best.points,
          peakHeightFt: best.peakHeightFt,
          estimatedArrivalSec: best.flightSec,
          launchAngleDeg: bestLaunchAngleDeg,
          initialSpeedMph: args.fixedSpeedMph,
          averageSpeedMph:
            best.flightSec > 0 ? (distance / best.flightSec) * 0.681818 : null,
          spinRpm,
          netClearanceFt: best.netClearanceFt,
          fitErrorFt: args.actualTrajectory?.length
            ? trajectoryFitErrorFt(best.points, args.actualTrajectory)
            : null,
        });
      }
      continue;
    }

    for (const launchAngleDeg of launchAngleDegrees) {
      const evaluate = (speedMph: number) => evaluateAt(launchAngleDeg, speedMph);

      const speedSamples: Array<{
        speedMph: number;
        missFt: number;
      }> = [];
      for (
        let speedMph = SOLVER_MIN_SPEED_MPH;
        speedMph <= SOLVER_MAX_SPEED_MPH;
        speedMph += SOLVER_SPEED_SCAN_STEP_MPH
      ) {
        const result = evaluate(speedMph);
        if (result.yAtTargetFt == null) continue;
        speedSamples.push({
          speedMph,
          missFt: result.yAtTargetFt - target.y,
        });
      }

      for (let sampleIndex = 1; sampleIndex < speedSamples.length; sampleIndex += 1) {
        const previousSample = speedSamples[sampleIndex - 1];
        const currentSample = speedSamples[sampleIndex];
        if (!previousSample || !currentSample) continue;
        if (previousSample.missFt * currentSample.missFt > 0) continue;

        let lo = previousSample.speedMph;
        let hi = currentSample.speedMph;
        let loMissFt = previousSample.missFt;
        let best = evaluate(hi);
        let bestSpeedMph = hi;
        for (let i = 0; i < 20; i += 1) {
          const mid = (lo + hi) / 2;
          const midResult = evaluate(mid);
          if (midResult.yAtTargetFt == null) break;
          const midMissFt = midResult.yAtTargetFt - target.y;
          best = midResult;
          bestSpeedMph = mid;
          if (loMissFt * midMissFt <= 0) {
            hi = mid;
          } else {
            lo = mid;
            loMissFt = midMissFt;
          }
        }

        if (best.flightSec == null || best.points.length < 3) continue;
        if (
          !args.allowNetHit &&
          shouldCrossNet &&
          (best.netClearanceFt == null ||
            best.netClearanceFt <= MIN_SOLVED_NET_CLEARANCE_FT)
        ) {
          continue;
        }
        solved.push({
          label: "Path",
          color: PHYSICS_PATH_COLORS[0],
          trajectory: best.points,
          peakHeightFt: best.peakHeightFt,
          estimatedArrivalSec: best.flightSec,
          launchAngleDeg,
          initialSpeedMph: bestSpeedMph,
          averageSpeedMph:
            best.flightSec > 0 ? (distance / best.flightSec) * 0.681818 : null,
          spinRpm,
          netClearanceFt: best.netClearanceFt,
          fitErrorFt: args.actualTrajectory?.length
            ? trajectoryFitErrorFt(best.points, args.actualTrajectory)
            : null,
        });
      }
    }
  }

  const minPeakGapFt = args.sortByFitError ? 0.65 : 0.35;
  const minSpeedGapMph = args.sortByFitError ? 2.5 : 1.1;
  const isDistinctFromPicked = (
    candidate: ShapeFanVariant,
    picked: ShapeFanVariant[],
  ) =>
    picked.every(
      (previous) =>
        Math.abs(previous.peakHeightFt - candidate.peakHeightFt) > minPeakGapFt ||
        Math.abs(previous.initialSpeedMph - candidate.initialSpeedMph) > minSpeedGapMph ||
        Math.abs((previous.netClearanceFt ?? 0) - (candidate.netClearanceFt ?? 0)) > 0.65,
    );

  if (args.variantPicks?.length) {
    const byNetClearance = solved.filter(
      (candidate) => !shouldCrossNet || candidate.netClearanceFt != null,
    );
    const picked: ShapeFanVariant[] = [];
    for (const pick of args.variantPicks) {
      const pool = byNetClearance.filter(
        (candidate) =>
          (pick.allowSimilar || isDistinctFromPicked(candidate, picked)) &&
          (pick.maxNetClearanceFt == null ||
            candidate.netClearanceFt == null ||
            candidate.netClearanceFt <= pick.maxNetClearanceFt) &&
          (pick.minPeakHeightFt == null ||
            candidate.peakHeightFt >= pick.minPeakHeightFt),
      );
      if (!pool.length) continue;

      const targetClearance = pick.targetNetClearanceFt;
      const rankedPool = pick.preferMaxSpeedClosestNet
        ? (() => {
            const fastest = pool.reduce(
              (maxSpeed, candidate) =>
                Math.max(maxSpeed, candidate.averageSpeedMph ?? candidate.initialSpeedMph),
              0,
            );
            const tolerance = pick.speedTieToleranceMph ?? 0;
            return pool.filter(
              (candidate) =>
                fastest - (candidate.averageSpeedMph ?? candidate.initialSpeedMph) <=
                tolerance,
            );
          })()
        : pool;
      const selected = [...rankedPool].sort((a, b) => {
        if (pick.preferMaxSpeedClosestNet) {
          const aClearance = a.netClearanceFt ?? Number.POSITIVE_INFINITY;
          const bClearance = b.netClearanceFt ?? Number.POSITIVE_INFINITY;
          const clearanceDiff = aClearance - bClearance;
          if (Math.abs(clearanceDiff) > 0.001) return clearanceDiff;
          const aSpeed = a.averageSpeedMph ?? a.initialSpeedMph;
          const bSpeed = b.averageSpeedMph ?? b.initialSpeedMph;
          const speedDiff = bSpeed - aSpeed;
          if (Math.abs(speedDiff) > 0.05) return speedDiff;
          return a.peakHeightFt - b.peakHeightFt;
        }
        if (pick.preferSlowClosestNet) {
          const aSpeed = a.averageSpeedMph ?? a.initialSpeedMph;
          const bSpeed = b.averageSpeedMph ?? b.initialSpeedMph;
          const speedDiff = aSpeed - bSpeed;
          if (Math.abs(speedDiff) > 0.05) return speedDiff;
          const aClearance = a.netClearanceFt ?? Number.POSITIVE_INFINITY;
          const bClearance = b.netClearanceFt ?? Number.POSITIVE_INFINITY;
          const clearanceDiff = aClearance - bClearance;
          if (Math.abs(clearanceDiff) > 0.001) return clearanceDiff;
          return a.peakHeightFt - b.peakHeightFt;
        }
        if (pick.preferLob) {
          const peakDiff = b.peakHeightFt - a.peakHeightFt;
          if (Math.abs(peakDiff) > 0.05) return peakDiff;
        }
        if (targetClearance != null) {
          const aClearance = a.netClearanceFt ?? targetClearance;
          const bClearance = b.netClearanceFt ?? targetClearance;
          const clearanceDiff =
            Math.abs(aClearance - targetClearance) -
            Math.abs(bClearance - targetClearance);
          if (Math.abs(clearanceDiff) > 0.001) return clearanceDiff;
        }
        return a.initialSpeedMph - b.initialSpeedMph;
      })[0];
      if (selected) {
        picked.push({
          ...selected,
          label: pick.label,
          color: pick.color,
        });
      }
    }
    return picked.slice(0, args.maxCount ?? args.variantPicks.length);
  }

  const deduped = solved
    .sort((a, b) => {
      if (args.sortByFitError) {
        const aError = a.fitErrorFt ?? Number.POSITIVE_INFINITY;
        const bError = b.fitErrorFt ?? Number.POSITIVE_INFINITY;
        if (Math.abs(aError - bError) > 0.001) return aError - bError;
      }
      return a.peakHeightFt - b.peakHeightFt;
    })
    .filter((candidate, index, candidates) => {
      if (index === 0) return true;
      return candidates
        .slice(0, index)
        .every(
          (previous) =>
            Math.abs(previous.peakHeightFt - candidate.peakHeightFt) > minPeakGapFt ||
            Math.abs(previous.initialSpeedMph - candidate.initialSpeedMph) >
              minSpeedGapMph,
        );
    })
    .slice(0, args.maxCount ?? 5);

  const labels = args.labels ?? [
    "Path A",
    "Path B",
    "Path C",
    "Path D",
    "Path E",
  ];
  const colors = args.colors ?? PHYSICS_PATH_COLORS;
  return deduped.map((variant, index) => ({
    ...variant,
    label: labels[index] ?? `Path ${index + 1}`,
    color: colors[index] ?? "#d9d9d9",
  }));
}

function spinStyleForRpm(spinRpm: number): "backspin" | "neutral" | "topspin" {
  if (spinRpm < -100) return "backspin";
  if (Math.abs(spinRpm) < 900) return "neutral";
  return "topspin";
}

function buildBestDropVariant(args: {
  start: Vector3 | null;
  target: Vector3 | null;
}): ShapeFanVariant[] {
  const commonArgs = {
    start: args.start,
    target: args.target,
    variantPicks: [
      {
        label: "Best drop",
        color: "#ffd36a",
        preferSlowClosestNet: true,
        allowSimilar: true,
      },
    ],
    spinRpmBuckets: [-300, -600, -800, -1000, -1200, -1400, -1600],
    launchAngleDegrees: makeNumberRange(3, 64, 1),
    maxCount: 1,
  };

  for (const maxNetClearanceFt of [2, 3.25, 5, null]) {
    const variants = buildPhysicsFitVariants({
      ...commonArgs,
      variantPicks: commonArgs.variantPicks.map((pick) => ({
        ...pick,
        ...(maxNetClearanceFt == null ? {} : { maxNetClearanceFt }),
      })),
    });
    if (variants.length) return variants;
  }
  return [];
}

function buildShapeFanVariants(option: ShotUniverseOption | null): ShapeFanVariant[] {
  const start = option?.trajectory[0] ?? null;
  const target = option?.target ?? null;
  const distanceFt = start && target ? Math.hypot(target.x - start.x, target.z - start.z) : null;
  const nearNetTarget =
    target != null && Math.abs(target.z) <= 8.25 && (distanceFt == null || distanceFt <= 18);
  const normalVariantPicks: ShapeFanVariantPick[] = [
    { label: "Path A", color: "#7fd7ff", targetNetClearanceFt: 0.75 },
    { label: "Path B", color: "#8ee68e", targetNetClearanceFt: 3 },
    { label: "Path C", color: "#c5a3ff", targetNetClearanceFt: 5.5 },
    {
      label: "Max mph",
      color: "#ff5fb7",
      preferMaxSpeedClosestNet: true,
      speedTieToleranceMph: 1.25,
      allowSimilar: true,
    },
  ];
  if (SHOW_LOB_SHAPE_FAN_VARIANT) {
    normalVariantPicks.push({
      label: "Lob",
      color: "#ffcf4a",
      targetNetClearanceFt: 8,
      minPeakHeightFt: 10,
      preferLob: true,
    });
  }
  const normalVariants = buildPhysicsFitVariants({
    start,
    target,
    variantPicks: normalVariantPicks,
    maxCount: normalVariantPicks.length,
  });
  const labeledNormalVariants = normalVariants.map((variant, index) => {
    const spinStyle = spinStyleForRpm(variant.spinRpm);
    if (variant.label === "Max mph") {
      return {
        ...variant,
        label: `Max mph (${spinStyle})`,
      };
    }
    return {
      ...variant,
      label: `${String.fromCharCode(65 + index)} (${spinStyle})`,
    };
  });
  const bestDrop = buildBestDropVariant({
    start,
    target,
  });
  const labeledBestDrop = bestDrop.map((variant) => ({
    ...variant,
    label: "Best drop (backspin)",
  }));
  const backspinDrop = nearNetTarget
    ? buildPhysicsFitVariants({
        start,
        target,
        variantPicks: [
          { label: "Drop (backspin)", color: "#ffd36a", targetNetClearanceFt: 0.8 },
        ],
        spinRpmBuckets: [-600, -800, -1000, -1200, -1400, -1600],
        launchAngleDegrees: makeNumberRange(8, 64, 1),
        maxCount: 1,
      })
    : [];
  const labeledBackspinDrop = backspinDrop.map((variant, index) => ({
    ...variant,
    label: `${String.fromCharCode(
      65 + labeledNormalVariants.length + labeledBestDrop.length + index,
    )} (backspin)`,
  }));
  return [...labeledNormalVariants, ...labeledBestDrop, ...labeledBackspinDrop];
}

function truncateVariantAtNetIfNeeded(variant: ShapeFanVariant): ShapeFanVariant {
  if (variant.netClearanceFt == null || variant.netClearanceFt > 0) return variant;
  const points = variant.trajectory;
  if (points.length < 2) return variant;
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    const crossesNet =
      (previous.z <= 0 && current.z >= 0) || (previous.z >= 0 && current.z <= 0);
    if (!crossesNet) continue;
    const span = current.z - previous.z;
    const t = Math.abs(span) < 0.001 ? 0 : (0 - previous.z) / span;
    const netPoint = previous.clone().lerp(current, clampNumber(t, 0, 1));
    const truncated = [...points.slice(0, i), netPoint];
    const fullDistances = cumulativeHorizontalDistances(points);
    const truncatedDistances = cumulativeHorizontalDistances(truncated);
    const fullDistance = fullDistances[fullDistances.length - 1] ?? 0;
    const truncatedDistance = truncatedDistances[truncatedDistances.length - 1] ?? 0;
    const timeScale = fullDistance > 0 ? clampNumber(truncatedDistance / fullDistance, 0.08, 1) : 1;
    return {
      ...variant,
      trajectory: truncated,
      estimatedArrivalSec: variant.estimatedArrivalSec * timeScale,
      stoppedAtNet: true,
    };
  }
  return variant;
}

function approximateTrajectoryNetClearance(points: Vector3[]): number | null {
  for (let i = 1; i < points.length; i += 1) {
    const previous = points[i - 1];
    const current = points[i];
    const crossesNet =
      (previous.z <= 0 && current.z >= 0) || (previous.z >= 0 && current.z <= 0);
    if (!crossesNet) continue;
    const span = current.z - previous.z;
    const t = Math.abs(span) < 0.001 ? 0 : (0 - previous.z) / span;
    return previous.y + (current.y - previous.y) * clampNumber(t, 0, 1) - NET_HEIGHT_FT;
  }
  return null;
}

function buildFallbackCustomShapeFanVariant(
  option: ShotUniverseOption,
  control: CustomShapeFanControl,
  lockedLabel: string,
  geometry?: CustomShapeFanGeometry | null,
): ShapeFanVariant | null {
  const start = geometry?.start ?? option.trajectory[0] ?? null;
  const target = geometry?.target ?? option.target ?? null;
  if (!start || !target) return null;
  const direction = new Vector3(target.x - start.x, 0, target.z - start.z);
  if (direction.length() <= 0.1) return null;
  direction.normalize();

  const trajectory = simulateTopspinTrajectoryToGround({
    start,
    direction,
    launchAngleDeg: control.angle,
    speedMph: control.mph,
    spinRpm: control.rpm,
  });
  if (trajectory.length < 2) return null;

  const distances = cumulativeHorizontalDistances(trajectory);
  const distanceFt = distances[distances.length - 1] ?? 0;
  const flightSec = control.mph > 0 ? clampNumber(distanceFt / (control.mph * 1.46667), 0.12, 4) : 1;
  const peakHeightFt = Math.max(start.y, ...trajectory.map((point) => point.y));
  const netClearanceFt = approximateTrajectoryNetClearance(trajectory);
  return truncateVariantAtNetIfNeeded({
    label: `Custom (${lockedLabel})`,
    color: CUSTOM_PATH_COLOR,
    trajectory,
    peakHeightFt,
    estimatedArrivalSec: flightSec,
    launchAngleDeg: control.angle,
    initialSpeedMph: control.mph,
    averageSpeedMph: flightSec > 0 ? (distanceFt / flightSec) * 0.681818 : null,
    spinRpm: control.rpm,
    netClearanceFt,
    isCustomPath: true,
    missesTarget: true,
    cannotHitTarget: true,
  });
}

function buildCustomShapeFanVariant(
  option: ShotUniverseOption | null,
  control: CustomShapeFanControl,
  geometry?: CustomShapeFanGeometry | null,
): ShapeFanVariant | null {
  const start = geometry?.start ?? option?.trajectory[0] ?? null;
  const target = geometry?.target ?? option?.target ?? null;
  if (!option || !start || !target) return null;

  const common = {
    start,
    target,
    variantPicks: [
      {
        label: `Custom (${spinStyleForRpm(control.rpm)})`,
        color: CUSTOM_PATH_COLOR,
        targetNetClearanceFt: 1.1,
      },
    ],
    maxCount: 1,
    allowNetHit: true,
  };
  const variants =
    control.mode === "rpm"
      ? buildPhysicsFitVariants({
          ...common,
          spinRpmBuckets: [control.rpm],
        })
      : control.mode === "angle"
        ? buildPhysicsFitVariants({
            ...common,
            launchAngleDegrees: [control.angle],
            spinRpmBuckets: CUSTOM_SPIN_RPM_BUCKETS,
          })
        : buildPhysicsFitVariants({
            ...common,
            fixedSpeedMph: control.mph,
            spinRpmBuckets: CUSTOM_SPIN_RPM_BUCKETS,
          });
  const variant = variants[0] ?? null;
  const lockedLabel =
    control.mode === "rpm"
      ? `${formatSpinRpm(control.rpm)} rpm`
      : control.mode === "angle"
        ? `${control.angle.toFixed(0)}°`
        : `${control.mph.toFixed(1)} mph`;
  if (!variant) {
    return buildFallbackCustomShapeFanVariant(option, control, lockedLabel, geometry);
  }
  return truncateVariantAtNetIfNeeded({
    ...variant,
    label: `Custom (${lockedLabel})`,
    color: CUSTOM_PATH_COLOR,
    isCustomPath: true,
  });
}

function buildActualShapeFanVariant(context: ShotUniverseContext | null): ShapeFanVariant | null {
  const trajectory = orientTrajectoryFromContact(
    context?.actualTrajectory ?? [],
    context?.contactPoint ?? null,
  );
  if (trajectory.length < 2) return null;
  const selectedShot = context?.selectedShot as any;
  const movement = selectedShot?.rawShot?.resulting_ball_movement;
  const distances = cumulativeHorizontalDistances(trajectory);
  const distanceFt = distances[distances.length - 1] ?? 0;
  const durationSec = estimateActualShotDurationSec(context);
  const start = trajectory[0]!;
  const end = trajectory[trajectory.length - 1]!;
  const launchAngleDeg =
    distanceFt > 0 ? (Math.atan2(end.y - start.y, distanceFt) * 180) / Math.PI : 0;
  const speedMph = Number(movement?.speed);
  const displaySpeedMph =
    Number.isFinite(speedMph) && speedMph > 0
      ? speedMph
      : durationSec > 0 && distanceFt > 0
        ? distanceFt / durationSec / 1.46667
        : 0;
  return {
    label: "Actual shot",
    color: "#ffffff",
    trajectory,
    peakHeightFt: Math.max(start.y, ...trajectory.map((point) => point.y)),
    estimatedArrivalSec: durationSec,
    launchAngleDeg,
    initialSpeedMph: displaySpeedMph,
    averageSpeedMph: durationSec > 0 ? (distanceFt / durationSec) * 0.681818 : null,
    spinRpm: 0,
    netClearanceFt: approximateTrajectoryNetClearance(trajectory),
    isActualPath: true,
  };
}

function buildActualFitVariants(context: ShotUniverseContext | null): ShapeFanVariant[] {
  const actual = context?.actualTrajectory ?? [];
  if (actual.length < 2) return [];
  return buildPhysicsFitVariants({
    start: actual[0],
    target: actual[actual.length - 1],
    labels: ["Fit 1", "Fit 2", "Fit 3", "Fit 4", "Fit 5"],
    colors: ["#35e0ff", "#ff6fd8", "#7dff8a", "#ffe45f", "#b38cff"],
    actualTrajectory: actual,
    sortByFitError: true,
  });
}

function buildActualShotLandingExtrapolation(
  context: ShotUniverseContext | null,
): ActualLandingExtrapolation | null {
  const actual = context?.actualTrajectory ?? [];
  const start = actual[0] ?? null;
  const end = actual[actual.length - 1] ?? null;
  if (!start || !end || actual.length < 2) return null;

  const extrapolationStartDistance = Math.hypot(end.x - start.x, end.z - start.z);
  if (extrapolationStartDistance <= 1) return null;

  const bestFit = buildPhysicsFitVariants({
    start,
    target: end,
    labels: ["Actual extrapolation"],
    colors: ["#ffffff"],
    maxCount: 1,
    actualTrajectory: actual,
    sortByFitError: true,
  })[0];
  if (!bestFit) return null;

  const direction = new Vector3(end.x - start.x, 0, end.z - start.z);
  if (direction.length() <= 0.01) return null;
  direction.normalize();

  const extended = simulateTopspinTrajectoryToGround({
    start,
    direction,
    launchAngleDeg: bestFit.launchAngleDeg,
    speedMph: bestFit.initialSpeedMph,
    spinRpm: bestFit.spinRpm,
  });
  if (extended.length < 2) return null;

  const extendedDistances = cumulativeHorizontalDistances(extended);
  const splicePoint =
    sampleTrajectoryAtDistance(extended, extendedDistances, extrapolationStartDistance) ??
    end;
  const alignmentOffset = end.clone().sub(splicePoint);
  const totalExtendedDistance =
    extendedDistances[extendedDistances.length - 1] ?? extrapolationStartDistance;
  const extraDistance = Math.max(
    0.1,
    totalExtendedDistance - extrapolationStartDistance,
  );
  const continuation = [end.clone()];
  for (let i = 1; i < extended.length; i += 1) {
    const distance = extendedDistances[i] ?? 0;
    if (distance > extrapolationStartDistance + 0.25) {
      const fadeDistance = Math.max(0, distance - extrapolationStartDistance);
      const offsetWeight = 1 - clampNumber(fadeDistance / extraDistance, 0, 1);
      continuation.push(
        extended[i]
          .clone()
          .add(alignmentOffset.clone().multiplyScalar(offsetWeight)),
      );
    }
  }

  const last = continuation[continuation.length - 1] ?? null;
  const continuationDistance = last
    ? Math.hypot(last.x - continuation[0]!.x, last.z - continuation[0]!.z)
    : 0;
  if (continuation.length < 2 || continuationDistance < 1 || !last) return null;

  const fitErrorFt =
    bestFit.fitErrorFt != null && Number.isFinite(bestFit.fitErrorFt)
      ? bestFit.fitErrorFt
      : null;
  const uncertaintyRadiusFt = clampNumber(
    0.55 + (fitErrorFt ?? 1.2) * 1.15 + continuationDistance * 0.045,
    0.75,
    4.25,
  );

  return {
    trajectory: continuation,
    landing: last.clone(),
    uncertaintyRadiusFt,
    fitErrorFt,
  };
}

function summarizeSelectedTarget(
  option: ShotUniverseOption | null,
): SelectedTargetSummary | null {
  if (!option) return null;
  const start = option.trajectory[0] ?? null;
  const distanceFt = start
    ? Math.hypot(option.target.x - start.x, option.target.z - start.z)
    : null;
  return {
    distanceFt,
  };
}

function summarizeActualFit(context: ShotUniverseContext | null): PhysicsFitSummary | null {
  const actual = context?.actualTrajectory ?? [];
  const start = actual[0] ?? null;
  const end = actual[actual.length - 1] ?? null;
  if (!start || !end) return null;
  return {
    distanceFt: Math.hypot(end.x - start.x, end.z - start.z),
  };
}

function estimateActualShotDurationSec(context: ShotUniverseContext | null): number {
  const actual = context?.actualTrajectory ?? [];
  const distances = cumulativeHorizontalDistances(actual);
  const trajectoryDistanceFt = distances[distances.length - 1] ?? 0;
  const selectedShot = context?.selectedShot as any;
  const movement = selectedShot?.rawShot?.resulting_ball_movement;
  const startMs = Number(
    selectedShot?.trajectoryStart?.ms ?? movement?.trajectory?.start?.ms,
  );
  const endMs = Number(
    selectedShot?.trajectoryEnd?.ms ?? movement?.trajectory?.end?.ms,
  );

  if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
    const durationSec = (endMs - startMs) / 1000;
    if (durationSec >= 0.08 && durationSec <= 4) return durationSec;
  }

  const payloadDistanceFt = Number(movement?.distance);
  const speedMph = Number(movement?.speed);
  const distanceFt =
    Number.isFinite(payloadDistanceFt) && payloadDistanceFt > 0
      ? payloadDistanceFt
      : trajectoryDistanceFt;
  if (Number.isFinite(speedMph) && speedMph > 1 && distanceFt > 0) {
    return clampNumber(distanceFt / (speedMph * 1.46667), 0.12, 4);
  }

  return clampNumber(trajectoryDistanceFt / 30, 0.35, 2.5);
}

function actualShotAnimationDurationSec(
  context: ShotUniverseContext | null,
  stableCourtOrbit: boolean,
): number {
  const durationSec = estimateActualShotDurationSec(context);
  if (!stableCourtOrbit) return durationSec;
  return clampNumber(durationSec * 0.55, 0.45, 1.15);
}

function buildPostBounceContinuation(points: Vector3[]): Vector3[] {
  const landing = points[points.length - 1] ?? null;
  const previous = points[points.length - 2] ?? null;
  if (!landing || !previous) return [];

  const direction = new Vector3(landing.x - previous.x, 0, landing.z - previous.z);
  if (direction.length() < 0.1) return [];
  direction.normalize();

  const baselineZ = direction.z >= 0 ? 22 : -22;
  const tToBaseline =
    Math.abs(direction.z) > 0.03 ? (baselineZ - landing.z) / direction.z : 8;
  const carryDistance = clampNumber(
    Number.isFinite(tToBaseline) && tToBaseline > 0
      ? Math.min(tToBaseline, 16)
      : 8,
    3,
    16,
  );
  const samples = 10;
  const out: Vector3[] = [];
  for (let i = 1; i <= samples; i += 1) {
    const t = i / samples;
    const distance = carryDistance * t;
    const lift = Math.max(0.06, 0.68 * Math.sin(Math.PI * t) * (1 - t * 0.34));
    out.push(
      new Vector3(
        landing.x + direction.x * distance,
        lift,
        landing.z + direction.z * distance,
      ),
    );
  }
  return out;
}

function displayTrajectoryForShapeFanVariant(variant: ShapeFanVariant): Vector3[] {
  if (variant.isActualPath || variant.isCustomPath) {
    return variant.trajectory;
  }
  if (!EXTEND_SHAPE_FAN_AFTER_BOUNCE) {
    return variant.trajectory;
  }
  const continuation = buildPostBounceContinuation(variant.trajectory);
  return continuation.length ? [...variant.trajectory, ...continuation] : variant.trajectory;
}

function firstCourtDepthCrossingDistance(
  trajectory: Vector3[],
  cumulative: number[],
  courtDepthZ = 0,
): number | null {
  for (let i = 1; i < trajectory.length; i += 1) {
    const previous = trajectory[i - 1]!;
    const current = trajectory[i]!;
    const crosses =
      (previous.z <= courtDepthZ && current.z >= courtDepthZ) ||
      (previous.z >= courtDepthZ && current.z <= courtDepthZ);
    if (!crosses) continue;

    const dz = current.z - previous.z;
    const t = Math.abs(dz) < 0.001 ? 0 : clampNumber((courtDepthZ - previous.z) / dz, 0, 1);
    return (
      (cumulative[i - 1] ?? 0) +
      Math.hypot(current.x - previous.x, current.z - previous.z) * t
    );
  }
  return null;
}

function estimatePathReaction(
  variant: ShapeFanVariant,
  context: ShotUniverseContext | null,
): PathReactionEstimate | null {
  if (!context?.playerPositions.length || variant.trajectory.length < 2) return null;
  const hitterTeam = context.hitterPlayerId != null && context.hitterPlayerId >= 2 ? 1 : 0;
  const opponents = context.playerPositions.filter((player) => player.teamId !== hitterTeam);
  if (!opponents.length) return null;

  const cumulative = cumulativeHorizontalDistances(variant.trajectory);
  const totalDistance = cumulative[cumulative.length - 1] ?? 0;
  if (totalDistance <= 0 || variant.estimatedArrivalSec <= 0) return null;
  const netCrossingDistance = firstCourtDepthCrossingDistance(
    variant.trajectory,
    cumulative,
    0,
  );
  const minimumConsideredDistance =
    netCrossingDistance == null ? 0 : Math.min(totalDistance, netCrossingDistance + 0.5);

  let best: PathReactionEstimate | null = null;
  for (let i = 1; i < variant.trajectory.length; i += 1) {
    const previous = variant.trajectory[i - 1];
    const current = variant.trajectory[i];
    const dx = current.x - previous.x;
    const dz = current.z - previous.z;
    const segmentDistance = Math.hypot(dx, dz);
    const denom = dx * dx + dz * dz;
    if (denom <= 0.001 || segmentDistance <= 0.001) continue;

    for (const opponent of opponents) {
      const t = clampNumber(
        ((opponent.position.x - previous.x) * dx +
          (opponent.position.z - previous.z) * dz) /
          denom,
        0,
        1,
      );
      const xAtClosest = previous.x + dx * t;
      const zAtClosest = previous.z + dz * t;
      const yAtClosest = previous.y + (current.y - previous.y) * t;
      const lateralDistanceFt = Math.hypot(
        xAtClosest - opponent.position.x,
        zAtClosest - opponent.position.z,
      );
      const distanceAtClosest = (cumulative[i - 1] ?? 0) + segmentDistance * t;
      if (distanceAtClosest < minimumConsideredDistance) continue;
      const timeSec =
        (distanceAtClosest / totalDistance) * variant.estimatedArrivalSec;
      if (timeSec < 0.08) continue;

      const estimate = {
        playerId: opponent.playerId,
        timeSec,
        lateralDistanceFt,
        ballHeightFt: yAtClosest,
      };
      if (
        !best ||
        estimate.lateralDistanceFt < best.lateralDistanceFt - 0.15 ||
        (Math.abs(estimate.lateralDistanceFt - best.lateralDistanceFt) <= 0.15 &&
          estimate.timeSec < best.timeSec)
      ) {
        best = estimate;
      }
    }
  }

  return best;
}

function formatSpinRpm(spinRpm: number): string {
  const absSpinRpm = Math.abs(spinRpm);
  return (
    absSpinRpm >= 1000
      ? `${(absSpinRpm / 1000).toFixed(absSpinRpm % 1000 === 0 ? 0 : 1)}k`
      : `${Math.round(absSpinRpm)}`
  );
}

function ShapeFanVariantMiniPlot(props: {
  variant: ShapeFanVariant;
  highlighted?: boolean;
  reactionText?: string;
}) {
  const trajectory = displayTrajectoryForShapeFanVariant(props.variant);
  if (trajectory.length < 2) return null;

  const width = 390;
  const height = 38;
  const padX = 8;
  const padRight = props.reactionText ? 48 : padX;
  const padTop = 5;
  const padBottom = 8;
  const courtMinZ = -22;
  const courtMaxZ = 22;

  const firstPoint = trajectory[0]!;
  const lastPoint = trajectory[trajectory.length - 1]!;
  const maxHeightFt = Math.max(
    10,
    props.variant.peakHeightFt,
    ...trajectory.map((point) => point.y),
  );
  const xScale = (courtDepthZ: number) =>
      padX +
      ((clampNumber(courtDepthZ, courtMinZ, courtMaxZ) - courtMinZ) /
        (courtMaxZ - courtMinZ)) *
      (width - padX - padRight);
  const yScale = (heightFt: number) =>
    height -
    padBottom -
    (clampNumber(heightFt, 0, maxHeightFt) / maxHeightFt) *
      (height - padTop - padBottom);
  const path = trajectory
    .map((point, index) => {
      const x = xScale(point.z);
      const y = yScale(point.y);
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const netX = xScale(0);
  const groundY = yScale(0);
  const netTopY = yScale(NET_HEIGHT_FT);
  const curveStrokeWidth = props.highlighted ? 4.2 : 2.4;
  const markerRadius = props.highlighted ? 4.2 : 2.5;
  const movingBallRadius = props.highlighted ? 4.8 : 3.4;
  const startX = xScale(firstPoint.z);
  const startY = yScale(firstPoint.y);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <line
        x1={padX}
        y1={height - padBottom}
        x2={width - padRight}
        y2={height - padBottom}
        stroke="rgba(255,255,255,0.2)"
        strokeWidth="1"
      />
      {[courtMinZ, courtMaxZ].map((courtDepthZ) => {
        const x = xScale(courtDepthZ);
        return (
          <line
            key={`baseline-${courtDepthZ}`}
            x1={x}
            y1={padTop}
            x2={x}
            y2={height - padBottom}
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="1"
            strokeDasharray="2 3"
          />
        );
      })}
      {[-7, 7].map((courtDepthZ) => {
        const x = xScale(courtDepthZ);
        return (
          <line
            key={`kitchen-${courtDepthZ}`}
            x1={x}
            y1={padTop}
            x2={x}
            y2={height - padBottom}
            stroke="rgba(255,255,255,0.26)"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        );
      })}
      <line
        x1={netX}
        y1={padTop}
        x2={netX}
        y2={height - padBottom}
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="1"
      />
      <line
        x1={netX}
        y1={netTopY}
        x2={netX}
        y2={groundY}
        stroke={props.variant.stoppedAtNet ? "rgba(255,128,104,0.82)" : "rgba(255,255,255,0.58)"}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {props.variant.stoppedAtNet ? (
        <g>
          <line
            x1={padX}
            y1={padTop + 2}
            x2={width - padRight}
            y2={height - padBottom - 2}
            stroke="rgba(255,128,104,0.34)"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <text
            x={width - padRight - 4}
            y={padTop + 9}
            fill="rgba(255,165,138,0.95)"
            fontSize="10"
            fontWeight="900"
            textAnchor="end"
          >
            NET
          </text>
        </g>
      ) : props.variant.cannotHitTarget ? (
        <text
          x={width - padRight - 4}
          y={padTop + 9}
          fill="rgba(255,255,255,0.78)"
          fontSize="10"
          fontWeight="900"
          textAnchor="end"
        >
          CAN'T HIT TARGET
        </text>
      ) : null}
      <path
        d={path}
        fill="none"
        stroke={props.variant.color}
        strokeWidth={curveStrokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={props.highlighted ? "1" : "0.95"}
      />
      <circle
        cx={startX}
        cy={startY}
        r={markerRadius}
        fill="#ffffff"
      />
      <circle
        cx={xScale(lastPoint.z)}
        cy={yScale(lastPoint.y)}
        r={markerRadius}
        fill={props.variant.color}
      />
      <circle r={movingBallRadius} fill={props.variant.color}>
        <animateMotion
          dur={`${Math.max(0.7, props.variant.estimatedArrivalSec * 1.35).toFixed(2)}s`}
          repeatCount="indefinite"
          path={path}
        />
      </circle>
      {props.reactionText ? (
        <text
          x={width - padX}
          y={height - 4}
          fill="rgba(255,255,255,0.9)"
          fontSize="10.5"
          fontWeight="900"
          textAnchor="end"
        >
          {props.reactionText}
        </text>
      ) : null}
    </svg>
  );
}

function MovementArea(props: {
  from: Vector3;
  to: Vector3;
  color: string;
}) {
  const dx = props.to.x - props.from.x;
  const dz = props.to.z - props.from.z;
  const length = Math.hypot(dx, dz);
  if (length < 0.15) return null;

  const midX = (props.from.x + props.to.x) / 2;
  const midZ = (props.from.z + props.to.z) / 2;
  const angleY = Math.atan2(dx, dz);
  const width = Math.min(2.15, Math.max(1.05, length * 0.22));

  return (
    <group>
      <mesh
        position={[midX, 0.12, midZ]}
        rotation={[0, angleY, 0]}
      >
        <boxGeometry args={[width, 0.05, length]} />
        <meshBasicMaterial color={props.color} transparent opacity={0.22} />
      </mesh>
      <mesh position={[props.from.x, 0.14, props.from.z]}>
        <cylinderGeometry args={[width * 0.46, width * 0.46, 0.06, 28]} />
        <meshBasicMaterial color={props.color} transparent opacity={0.22} />
      </mesh>
      <mesh position={[props.to.x, 0.14, props.to.z]}>
        <cylinderGeometry args={[width * 0.56, width * 0.56, 0.07, 28]} />
        <meshBasicMaterial color={props.color} transparent opacity={0.38} />
      </mesh>
    </group>
  );
}

function ShotUniverseScene(props: {
  context: ShotUniverseContext;
  movementContext?: ShotUniverseContext | null;
  lingeringActualContext?: ShotUniverseContext | null;
  lingeringActualOpacity?: number;
  lingeringActualExtrapolation?: ActualLandingExtrapolation | null;
  shotFamily: ShotUniverseShotFamily;
  showActual: boolean;
  showGhosts: boolean;
  showTargets: boolean;
  cameraView: CameraView;
  viewBleedPx?: number;
  stableCourtOrbit: boolean;
  hideVideoAlignedCourtLines?: boolean;
  resetNonce: number;
  rotationSteps: number;
  interactive: boolean;
  interactionBlockedRef?: React.RefObject<boolean>;
  visibility: ShotUniverseVisibility;
  onCameraInteraction?: () => void;
  onVideoCourtProjection?: (projection: ShotUniverseVideoCourtProjection | null) => void;
  selectedShapeFanOptionId: string | null;
  shapeFanVariants: ShapeFanVariant[];
  hoveredShapeFanLabel: string | null;
  selectedShapeFanLabel: string | null;
  focusCustomTrajectoryOnly?: boolean;
  actualLandingExtrapolation?: ActualLandingExtrapolation | null;
  actualFitVariants: ShapeFanVariant[];
  hoveredActualFitLabel: string | null;
  onSelectShapeFanOption: (optionId: string) => void;
  selectedShapeFanOptionIds: Set<string>;
  onHoverShapeFanVariant: (label: string | null) => void;
  onSelectShapeFanVariant: (label: string | null) => void;
  onHoverActualFitVariant: (label: string | null) => void;
}) {
  const controlsRef = React.useRef<any>(null);
  const rotatedSceneRef = React.useRef<Group | null>(null);
  const sceneYawRef = React.useRef(0);
  const cameraViewRef = React.useRef(props.cameraView);
  const userControlActiveRef = React.useRef(false);
  const cameraInteractionStartRef = React.useRef<null | {
    position: Vector3;
    target: Vector3;
  }>(null);
  const cameraInteractionReportedRef = React.useRef(false);
  const { camera, size } = useThree();
  const { onCameraInteraction } = props;
  const isSceneInteractive = React.useCallback(
    () => props.interactive && !props.interactionBlockedRef?.current,
    [props.interactive, props.interactionBlockedRef],
  );
  cameraViewRef.current = props.cameraView;
  const visibleOptions = props.context.options.filter(
    (option) => props.shotFamily === "all" || option.shotFamily === props.shotFamily,
  );
  const targetMarkerOptions = React.useMemo(() => {
    const markers: ShotUniverseOption[] = [];
    for (const option of visibleOptions) {
      const hasSameTarget = markers.some(
        (marker) =>
          Math.hypot(marker.target.x - option.target.x, marker.target.z - option.target.z) <
          0.2,
      );
      if (!hasSameTarget) markers.push(option);
    }
    if (
      props.focusCustomTrajectoryOnly &&
      (props.selectedShapeFanOptionId || props.selectedShapeFanOptionIds.size > 0)
    ) {
      const focusedMarkers: ShotUniverseOption[] = [];
      const addMarkerForOptionId = (optionId: string | null) => {
        if (!optionId) return;
        const option = visibleOptions.find((candidate) => candidate.id === optionId);
        const marker =
          markers.find((candidate) => candidate.id === optionId) ??
          markers.find((candidate) => {
            return (
              option != null &&
              Math.hypot(
                candidate.target.x - option.target.x,
                candidate.target.z - option.target.z,
              ) < 0.2
            );
          }) ??
          null;
        if (
          marker &&
          !focusedMarkers.some(
            (candidate) =>
              Math.hypot(
                candidate.target.x - marker.target.x,
                candidate.target.z - marker.target.z,
              ) < 0.2,
          )
        ) {
          focusedMarkers.push(marker);
        }
      };
      addMarkerForOptionId(props.selectedShapeFanOptionId);
      for (const optionId of Array.from(props.selectedShapeFanOptionIds)) {
        addMarkerForOptionId(optionId);
      }
      return focusedMarkers.length ? focusedMarkers : markers;
    }
    return markers;
  }, [
    props.focusCustomTrajectoryOnly,
    props.selectedShapeFanOptionId,
    props.selectedShapeFanOptionIds,
    visibleOptions,
  ]);
  const selectedOption =
    visibleOptions.find((option) => option.id === props.selectedShapeFanOptionId) ??
    null;
  const savedTargetIdList = Array.from(props.selectedShapeFanOptionIds);
  const savedReferenceOptions = props.focusCustomTrajectoryOnly
    ? visibleOptions.filter(
        (option) =>
          props.selectedShapeFanOptionIds.has(option.id) &&
          option.id !== props.selectedShapeFanOptionId,
      )
    : [];
  const shapeFanActive = selectedOption != null;
  const targetChoiceLabelPosition = React.useMemo<[number, number, number] | null>(() => {
    if (
      !props.showTargets ||
      !props.focusCustomTrajectoryOnly ||
      shapeFanActive ||
      targetMarkerOptions.length === 0
    ) {
      return null;
    }
    const sum = targetMarkerOptions.reduce(
      (acc, option) => {
        acc.x += option.target.x;
        acc.z += option.target.z;
        acc.maxY = Math.max(acc.maxY, option.target.y);
        return acc;
      },
      { x: 0, z: 0, maxY: 0 },
    );
    return [
      sum.x / targetMarkerOptions.length,
      Math.max(2.75, sum.maxY + 2.05),
      sum.z / targetMarkerOptions.length,
    ];
  }, [
    props.focusCustomTrajectoryOnly,
    props.showTargets,
    shapeFanActive,
    targetMarkerOptions,
  ]);
  const movementContext = props.movementContext ?? props.context;
  const visibility = props.visibility;
  const hitterTeam =
    movementContext.hitterPlayerId != null && movementContext.hitterPlayerId >= 2 ? 1 : 0;
  const currentOpponents = movementContext.playerPositions.filter(
    (player) => player.teamId !== hitterTeam,
  );
  const currentOpponentById = new Map(
    currentOpponents.map((player) => [player.playerId, player]),
  );
  const nextOpponentById = new Map(
    movementContext.nextOpponentPositions.map((player) => [player.playerId, player]),
  );

  React.useLayoutEffect(() => {
    const cameraView = cameraViewRef.current;
    sceneYawRef.current = props.rotationSteps * ROTATION_STEP_RADIANS;
    if (rotatedSceneRef.current) {
      rotatedSceneRef.current.rotation.y = sceneYawRef.current;
    }
    const target = props.stableCourtOrbit
      ? COURT_ORBIT_TARGET.clone()
      : new Vector3(...cameraView.target);
    const position = props.stableCourtOrbit
      ? target.clone().add(COURT_ORBIT_FALLBACK_OFFSET)
      : new Vector3(...cameraView.position);
    if (props.stableCourtOrbit) {
      const offset = position.clone().sub(target);
      const distance = clampNumber(
        offset.length(),
        COURT_ORBIT_MIN_DISTANCE,
        COURT_ORBIT_MAX_DISTANCE,
      );
      if (offset.y < 4) {
        offset.y = 5;
      }
      offset.setLength(distance);
      position.copy(target).add(offset);
    }
    camera.position.copy(position);
    if ("fov" in camera) {
      (camera as any).aspect = Math.max(1, size.width) / Math.max(1, size.height);
      (camera as any).fov = props.stableCourtOrbit ? COURT_ORBIT_FOV : cameraView.fov;
      if (!props.stableCourtOrbit && props.viewBleedPx) {
        const bleed = Math.max(0, props.viewBleedPx);
        const baseWidth = Math.max(1, size.width - bleed * 2);
        const baseHeight = Math.max(1, size.height - bleed * 2);
        (camera as any).setViewOffset?.(
          baseWidth,
          baseHeight,
          -bleed + cameraView.offset[0],
          -bleed + cameraView.offset[1],
          size.width,
          size.height,
        );
      } else {
        (camera as any).clearViewOffset?.();
      }
      (camera as any).updateProjectionMatrix?.();
    }
    controlsRef.current?.target.copy(target);
    controlsRef.current?.update();
    if (!props.stableCourtOrbit && cameraView.roll) {
      camera.rotateZ((cameraView.roll * Math.PI) / 180);
    }
    camera.updateMatrixWorld();

    if (props.onVideoCourtProjection) {
      if (props.stableCourtOrbit) {
        props.onVideoCourtProjection(null);
      } else {
        const bleed = Math.max(0, props.viewBleedPx ?? 0);
        const project = (point: Vector3) => {
          const projected = point.clone().project(camera);
          return `${(((projected.x + 1) / 2) * size.width - bleed).toFixed(1)},${(((1 - projected.y) / 2) * size.height - bleed).toFixed(1)}`;
        };
        const linePoints = (points: Vector3[]) => points.map(project).join(" ");
        props.onVideoCourtProjection({
          outer: linePoints([
            new Vector3(-10, 0, -22),
            new Vector3(10, 0, -22),
            new Vector3(10, 0, 22),
            new Vector3(-10, 0, 22),
            new Vector3(-10, 0, -22),
          ]),
          lines: [
            linePoints([new Vector3(-10, 0, 0), new Vector3(10, 0, 0)]),
            linePoints([new Vector3(-10, 0, -7), new Vector3(10, 0, -7)]),
            linePoints([new Vector3(-10, 0, 7), new Vector3(10, 0, 7)]),
            linePoints([new Vector3(0, 0, -22), new Vector3(0, 0, -7)]),
            linePoints([new Vector3(0, 0, 7), new Vector3(0, 0, 22)]),
          ],
        });
      }
    }
  }, [
    camera,
    props.cameraView,
    props.onVideoCourtProjection,
    props.resetNonce,
    props.rotationSteps,
    props.stableCourtOrbit,
    props.viewBleedPx,
    size.height,
    size.width,
  ]);

  const setTargetHovering = React.useCallback((hovering: boolean) => {
    document.body.style.cursor = hovering ? "pointer" : "";
    if (controlsRef.current) {
      controlsRef.current.enabled = !hovering;
    }
  }, []);

  React.useEffect(
    () => () => {
      document.body.style.cursor = "";
      if (controlsRef.current) {
        controlsRef.current.enabled = true;
      }
    },
    [],
  );

  return (
    <>
      <ambientLight intensity={0.9} />
      <directionalLight position={[0, 28, 18]} intensity={0.62} />
      <group ref={rotatedSceneRef} position={NET_MIDPOINT_FLOOR_ARRAY}>
        <group position={NEGATIVE_NET_MIDPOINT_FLOOR_ARRAY}>
          {props.stableCourtOrbit || !props.hideVideoAlignedCourtLines ? (
            <CourtLines videoAligned={!props.stableCourtOrbit} />
          ) : null}

          {movementContext.playerPositions.map((player) => (
            <group
              key={player.playerId}
              position={[player.position.x, player.position.y, player.position.z]}
            >
              <mesh position={[0, 0.08, 0]}>
                <cylinderGeometry args={[0.62, 0.62, 0.1, 24]} />
                <meshBasicMaterial color={playerColor(player.playerId)} transparent opacity={0.86} />
              </mesh>
              <mesh position={[0, 0.55, 0]}>
                <sphereGeometry args={[0.24, 12, 12]} />
                <meshBasicMaterial color={playerColor(player.playerId)} />
              </mesh>
            </group>
          ))}

      {movementContext.previousOpponentPositions.map((player) => {
        const current = currentOpponentById.get(player.playerId) ?? null;
        const color = playerColor(player.playerId);
        return (
          <group key={`prev-opponent-${player.playerId}`}>
            {current ? (
              <MovementArea from={player.position} to={current.position} color={color} />
            ) : null}
            <mesh position={[player.position.x, 0.1, player.position.z]}>
              <cylinderGeometry args={[0.95, 0.95, 0.08, 28]} />
              <meshBasicMaterial color={color} transparent opacity={0.22} />
            </mesh>
            <mesh position={[player.position.x, 0.18, player.position.z]}>
              <cylinderGeometry args={[0.46, 0.46, 0.1, 20]} />
              <meshBasicMaterial color={color} transparent opacity={0.72} />
            </mesh>
          </group>
        );
      })}

      {movementContext.movementCues.map((cue) => (
        <MovementArea
          key={`movement-cue-${cue.shotNumber}-${cue.nextShotNumber}-${cue.playerId}`}
          from={cue.from}
          to={cue.to}
          color={playerColor(cue.playerId)}
        />
      ))}

      {movementContext.nextOpponentPositions.map((player) => {
        const color = playerColor(player.playerId);
        return (
          <group key={`next-opponent-${player.playerId}`}>
            <mesh position={[player.position.x, 0.12, player.position.z]}>
              <cylinderGeometry args={[1.08, 1.08, 0.08, 30]} />
              <meshBasicMaterial color={color} transparent opacity={0.32} />
            </mesh>
            <mesh
              position={[player.position.x, 0.2, player.position.z]}
              rotation={[Math.PI / 2, 0, 0]}
            >
              <torusGeometry args={[0.74, 0.075, 8, 32]} />
              <meshBasicMaterial color={color} transparent opacity={0.96} />
            </mesh>
            <mesh position={[player.position.x, 0.18, player.position.z]}>
              <cylinderGeometry args={[0.3, 0.3, 0.08, 18]} />
              <meshBasicMaterial color={color} transparent opacity={0.66} />
            </mesh>
          </group>
        );
      })}

      {currentOpponents.map((player) => (
        <group key={`current-opponent-halo-${player.playerId}`}>
          <mesh position={[player.position.x, 0.16, player.position.z]}>
            <cylinderGeometry args={[0.86, 0.86, 0.08, 28]} />
            <meshBasicMaterial
              color={playerColor(player.playerId)}
              transparent
              opacity={0.28}
            />
          </mesh>
          {nextOpponentById.has(player.playerId) ? (
            <mesh
              position={[player.position.x, 0.24, player.position.z]}
              rotation={[Math.PI / 2, 0, 0]}
            >
              <torusGeometry args={[0.58, 0.045, 8, 28]} />
              <meshBasicMaterial
                color="#ffffff"
                transparent
                opacity={0.58}
              />
            </mesh>
          ) : null}
        </group>
      ))}

      {props.context.contactPoint ? (
        <mesh
          position={[
            props.context.contactPoint.x,
            props.context.contactPoint.y,
            props.context.contactPoint.z,
          ]}
        >
          <sphereGeometry args={[0.42, 14, 14]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
      ) : null}

      {props.showActual &&
      props.lingeringActualContext &&
      props.lingeringActualContext.actualTrajectory.length >= 2 &&
      (props.lingeringActualOpacity ?? 0) > 0 ? (
        <group>
          <TrajectoryTube
            points={props.lingeringActualContext.actualTrajectory}
            color="#ffffff"
            opacity={clampOpacity(props.lingeringActualOpacity ?? 0)}
            radius={visibility.actualTubeRadius * 1.18}
            roundedEnds
          />
          {props.lingeringActualExtrapolation &&
          props.lingeringActualExtrapolation.trajectory.length >= 2 ? (
            <ExtrapolationCone
              extrapolation={props.lingeringActualExtrapolation}
              dimmed
            />
          ) : null}
        </group>
      ) : null}

      {props.showActual && props.context.actualTrajectory.length >= 2 ? (
        <TrajectoryTube
          points={props.context.actualTrajectory}
          color="#ffffff"
          opacity={clampOpacity(
            shapeFanActive ? visibility.actualDimOpacity : visibility.actualOpacity,
          )}
          radius={visibility.actualTubeRadius}
          roundedEnds
        />
      ) : null}

      {props.showActual &&
      (shapeFanActive || props.stableCourtOrbit) &&
      props.context.actualTrajectory.length >= 2 ? (
        <AnimatedTrajectoryBall
          points={props.context.actualTrajectory}
          color={PICKLEBALL_YELLOW}
          durationSec={actualShotAnimationDurationSec(
            props.context,
            props.stableCourtOrbit,
          )}
          radius={props.stableCourtOrbit ? 0.34 : undefined}
          innerRadius={props.stableCourtOrbit ? 0.14 : undefined}
        />
      ) : null}

      {props.showActual &&
      (props.actualLandingExtrapolation?.trajectory.length ?? 0) >= 2 ? (
        <group>
          <ExtrapolationCone
            extrapolation={props.actualLandingExtrapolation!}
            dimmed={shapeFanActive}
          />
          <mesh
            position={[
              props.actualLandingExtrapolation!.landing.x,
              0.035,
              props.actualLandingExtrapolation!.landing.z,
            ]}
          >
            <cylinderGeometry
              args={[
                props.actualLandingExtrapolation!.uncertaintyRadiusFt,
                props.actualLandingExtrapolation!.uncertaintyRadiusFt,
                0.045,
                36,
              ]}
            />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={clampOpacity(shapeFanActive ? 0.34 : 0.6)}
              depthTest={false}
            />
          </mesh>
          <mesh
            position={[
              props.actualLandingExtrapolation!.landing.x,
              0.075,
              props.actualLandingExtrapolation!.landing.z,
            ]}
          >
            <cylinderGeometry
              args={[
                props.actualLandingExtrapolation!.uncertaintyRadiusFt * 0.18,
                props.actualLandingExtrapolation!.uncertaintyRadiusFt * 0.18,
                0.055,
                18,
              ]}
            />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={clampOpacity(shapeFanActive ? 0.38 : 0.68)}
              depthTest={false}
            />
          </mesh>
        </group>
      ) : null}

      {props.showGhosts && selectedOption && !props.shapeFanVariants.length ? (
        <LinePoints
          key={selectedOption.id}
          points={selectedOption.trajectory}
          color={optionColor(selectedOption)}
          opacity={clampOpacity(visibility.ghostSelectedOpacity)}
          lineWidth={2}
          onClick={(event) => {
            event.stopPropagation();
            props.onSelectShapeFanOption(selectedOption.id);
          }}
        />
      ) : null}

      {savedReferenceOptions.map((option) => (
        <group key={`saved-reference-path-${option.id}`}>
          <TrajectoryTube
            points={option.trajectory}
            color="#75f49b"
            opacity={0.74}
            radius={0.085}
            roundedEnds
            depthTest={false}
          />
          <LinePoints
            points={option.trajectory}
            color="#75f49b"
            opacity={0.94}
            lineWidth={7}
          />
          <AnimatedTrajectoryBall
            points={option.trajectory}
            color="#75f49b"
            durationSec={0.8}
            opacity={0.88}
            radius={0.23}
            innerRadius={0.09}
          />
        </group>
      ))}

      {props.shapeFanVariants.map((variant, index) => {
        const displayTrajectory = displayTrajectoryForShapeFanVariant(variant);
        const highlighted = shapeFanLabelMatchesVariant(
          props.hoveredShapeFanLabel,
          variant,
        );
        const selected = shapeFanLabelMatchesVariant(
          props.selectedShapeFanLabel,
          variant,
        );
        const emphasized = highlighted || selected;
        if (variant.isActualPath) return null;
        const dimmed =
          (props.hoveredShapeFanLabel != null || props.selectedShapeFanLabel != null) &&
          !highlighted &&
          !selected;
        const opacity = clampOpacity(
          emphasized
            ? variant.isCustomPath
              ? 0.78
              : 0.88
            : dimmed
              ? 0.16
              : variant.isCustomPath
                ? 0.7
                : visibility.shapeFanOpacityBase + index * visibility.shapeFanOpacityStep,
        );
        const pointerHandlers = {
          onClick: (event: any) => {
            event.stopPropagation();
            props.onSelectShapeFanVariant(selected ? null : variant.label);
          },
          onPointerOver: (event: any) => {
            event.stopPropagation();
            document.body.style.cursor = "pointer";
            props.onHoverShapeFanVariant(variant.label);
          },
          onPointerOut: (event: any) => {
            event.stopPropagation();
            document.body.style.cursor = "";
            props.onHoverShapeFanVariant(null);
          },
        };
        return (
          <group key={`shape-fan-${shapeFanVariantIdentity(variant)}`}>
            {emphasized ? (
              <TrajectoryTube
                points={displayTrajectory}
                color={variant.color}
                opacity={opacity}
                radius={variant.isCustomPath ? 0.105 : 0.13}
                roundedEnds
                depthTest
              />
            ) : null}
            <LinePoints
              points={displayTrajectory}
              color={variant.color}
              opacity={emphasized ? 0.85 : opacity}
              lineWidth={emphasized ? (variant.isCustomPath ? 10 : 13) : 2.4}
              {...pointerHandlers}
            />
            <AnimatedTrajectoryBall
              points={displayTrajectory}
              color={variant.color}
              durationSec={variant.estimatedArrivalSec}
              opacity={emphasized ? 0.98 : dimmed ? 0.36 : 0.58}
              radius={emphasized ? 0.29 : dimmed ? 0.17 : 0.21}
              innerRadius={emphasized ? 0.12 : dimmed ? 0.065 : 0.085}
            />
          </group>
        );
      })}

      {props.actualFitVariants.map((variant, index) => {
        const highlighted = props.hoveredActualFitLabel === variant.label;
        const dimmed = props.hoveredActualFitLabel != null && !highlighted;
        const opacity = clampOpacity(highlighted ? 0.9 : dimmed ? 0.08 : 0.28);
        const pointerHandlers = {
          onPointerOver: (event: any) => {
            event.stopPropagation();
            document.body.style.cursor = "pointer";
            props.onHoverActualFitVariant(variant.label);
          },
          onPointerOut: (event: any) => {
            event.stopPropagation();
            document.body.style.cursor = "";
            props.onHoverActualFitVariant(null);
          },
        };
        return (
          <group key={`actual-fit-${variant.label}`}>
            {highlighted ? (
              <TrajectoryTube
                points={variant.trajectory}
                color={variant.color}
                opacity={opacity}
                radius={0.16}
                roundedEnds
              />
            ) : null}
            <LinePoints
              points={variant.trajectory}
              color={variant.color}
              opacity={highlighted ? 0.9 : opacity}
              lineWidth={highlighted ? 12 : 1.25 + index * 0.1}
              {...pointerHandlers}
            />
          </group>
        );
      })}

      {props.showTargets
        ? targetMarkerOptions.map((option) => {
            const savedTargetIndex = savedTargetIdList.indexOf(option.id);
            const colocatedSavedTargetIndexes = savedTargetIdList
              .map((optionId, index) => {
                const savedOption = visibleOptions.find((candidate) => candidate.id === optionId);
                if (!savedOption) return null;
                return Math.hypot(
                  savedOption.target.x - option.target.x,
                  savedOption.target.z - option.target.z,
                ) < 0.2
                  ? index
                  : null;
              })
              .filter((index): index is number => index != null);
            const selected =
              selectedOption != null &&
              Math.hypot(
                selectedOption.target.x - option.target.x,
                selectedOption.target.z - option.target.z,
              ) < 0.2;
            const saved =
              props.selectedShapeFanOptionIds.has(option.id) ||
              colocatedSavedTargetIndexes.length > 0;
            const prominent = selected || saved;
            const targetFieldChoice =
              props.focusCustomTrajectoryOnly && !shapeFanActive && !prominent;
            const targetLabel =
              props.focusCustomTrajectoryOnly && colocatedSavedTargetIndexes.length > 1
                ? `Refs ${colocatedSavedTargetIndexes
                    .map((targetIndex) => shortReferenceLabelForIndex(targetIndex).replace(/^Ref /, ""))
                    .join("+")}`
                : props.focusCustomTrajectoryOnly && colocatedSavedTargetIndexes.length === 1
                  ? shortReferenceLabelForIndex(colocatedSavedTargetIndexes[0])
                  : props.focusCustomTrajectoryOnly && savedTargetIndex >= 0
                    ? shortReferenceLabelForIndex(savedTargetIndex)
                    : null;
            const labelTargetIndex =
              colocatedSavedTargetIndexes.length > 0
                ? colocatedSavedTargetIndexes[colocatedSavedTargetIndexes.length - 1]
                : savedTargetIndex;
            const shouldOffsetTargetLabel = targetLabel?.startsWith("Ref ") ?? false;
            const targetLabelOffsetX =
              shouldOffsetTargetLabel && savedTargetIdList.length > 1
                ? labelTargetIndex % 2 === 0
                  ? -1.35
                  : 1.35
                : null;
            return (
              <group
                key={`target-${option.id}`}
                position={[option.target.x, 0, option.target.z]}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onSelectShapeFanOption(option.id);
                }}
                onPointerOver={(event) => {
                  event.stopPropagation();
                  setTargetHovering(true);
                }}
                onPointerOut={() => {
                  setTargetHovering(false);
                }}
              >
                <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                  <ringGeometry
                    args={[
                      selected ? 0.72 : saved ? 0.52 : targetFieldChoice ? 0.46 : 0.24,
                      selected ? 1.05 : saved ? 0.78 : targetFieldChoice ? 0.72 : 0.38,
                      64,
                    ]}
                  />
                  <meshBasicMaterial
                    color={
                      selected ? PRACTICE_TARGET_ORANGE : saved ? "#8ee68e" : PRACTICE_TARGET_ORANGE
                    }
                    transparent
                    opacity={clampOpacity(
                      selected ? 1 : prominent ? 0.95 : targetFieldChoice ? 0.92 : 0.62,
                    )}
                    side={DoubleSide}
                  />
                </mesh>
                {prominent || targetFieldChoice ? (
                  <mesh
                    position={[
                      0,
                      selected
                        ? 1.05
                        : targetFieldChoice
                          ? 0.46
                          : Math.max(0.18, option.target.y / 2),
                      0,
                    ]}
                  >
                    <cylinderGeometry
                      args={[
                        selected ? 0.045 : targetFieldChoice ? 0.04 : 0.035,
                        selected ? 0.045 : targetFieldChoice ? 0.04 : 0.035,
                        selected
                          ? 2.1
                          : targetFieldChoice
                            ? 0.92
                            : Math.max(0.32, option.target.y),
                        12,
                      ]}
                    />
                    <meshBasicMaterial
                      color={
                        selected || targetFieldChoice ? PRACTICE_TARGET_ORANGE : "#8ee68e"
                      }
                      transparent
                      opacity={selected ? 1 : targetFieldChoice ? 0.86 : 0.9}
                    />
                  </mesh>
                ) : null}
                <mesh
                  position={[
                    0,
                    selected ? 2.18 : targetFieldChoice ? 0.96 : Math.max(0.16, option.target.y),
                    0,
                  ]}
                >
                  <sphereGeometry
                    args={[
                      selected ? 0.22 : saved ? 0.28 : targetFieldChoice ? 0.26 : 0.2,
                      16,
                      16,
                    ]}
                  />
                  <meshBasicMaterial
                    color={selected ? "#ffe45f" : saved ? "#8ee68e" : PRACTICE_TARGET_ORANGE}
                    transparent
                    opacity={clampOpacity(
                      selected || saved
                        ? 1
                        : targetFieldChoice
                          ? 0.95
                        : shapeFanActive
                          ? Math.max(visibility.targetDimOpacity, 0.68)
                          : visibility.targetOpacity,
                    )}
                  />
                </mesh>
                {targetLabel && prominent ? (
                  <TargetLabelSprite
                    label={targetLabel}
                    color={selected ? "rgba(255,228,95,0.95)" : "rgba(117,244,155,0.95)"}
                    background={selected ? "rgba(68,52,0,0.9)" : "rgba(0,52,22,0.9)"}
                    position={[
                      targetLabelOffsetX ?? 0,
                      selected ? 2.86 : Math.max(0.74, option.target.y + 0.46),
                      0,
                    ]}
                    renderOrder={selected ? 140 : 128 + Math.max(0, labelTargetIndex)}
                  />
                ) : null}
              </group>
            );
          })
        : null}
      {targetChoiceLabelPosition ? (
        <TargetLabelSprite
          label={`all ${targetMarkerOptions.length} target choices`}
          color="rgba(255,158,58,0.95)"
          background="rgba(72,38,0,0.9)"
          position={targetChoiceLabelPosition}
          scale={[15.6, 3.7, 1]}
          renderOrder={148}
        />
      ) : null}
        </group>
      </group>

      <OrbitControls
        ref={controlsRef}
        enableRotate={isSceneInteractive()}
        enablePan={props.stableCourtOrbit ? false : isSceneInteractive()}
        enableZoom={isSceneInteractive()}
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.72}
        zoomSpeed={0.42}
        minDistance={props.stableCourtOrbit ? COURT_ORBIT_MIN_DISTANCE : 1}
        maxDistance={props.stableCourtOrbit ? COURT_ORBIT_MAX_DISTANCE : 300}
        minPolarAngle={props.stableCourtOrbit ? 0.16 : 0}
        maxPolarAngle={props.stableCourtOrbit ? Math.PI / 2 - 0.05 : Math.PI}
        screenSpacePanning={false}
        makeDefault
        onStart={() => {
          if (!isSceneInteractive()) {
            userControlActiveRef.current = false;
            cameraInteractionReportedRef.current = true;
            cameraInteractionStartRef.current = null;
            return;
          }
          userControlActiveRef.current = true;
          cameraInteractionReportedRef.current = false;
          cameraInteractionStartRef.current = {
            position: camera.position.clone(),
            target: controlsRef.current?.target?.clone?.() ?? new Vector3(),
          };
          if (!props.stableCourtOrbit) {
            cameraInteractionReportedRef.current = true;
            onCameraInteraction?.();
          }
        }}
        onEnd={() => {
          userControlActiveRef.current = false;
          cameraInteractionStartRef.current = null;
        }}
        onChange={
          props.interactive
            ? () => {
                if (!isSceneInteractive()) return;
                if (props.stableCourtOrbit) return;
                if (
                  !userControlActiveRef.current ||
                  cameraInteractionReportedRef.current
                ) {
                  return;
                }
                const start = cameraInteractionStartRef.current;
                const currentTarget = controlsRef.current?.target ?? null;
                const cameraMoved =
                  !start ||
                  camera.position.distanceTo(start.position) > 0.05 ||
                  (currentTarget != null &&
                    currentTarget.distanceTo(start.target) > 0.05);
                if (!cameraMoved) return;
                cameraInteractionReportedRef.current = true;
                onCameraInteraction?.();
              }
            : undefined
        }
      />
    </>
  );
}

function fovToVerticalDegrees(fov: unknown): number {
  const n = Number(fov);
  if (!Number.isFinite(n) || n <= 0) return 68;
  const horizontalDegrees = n <= Math.PI * 2 ? (n * 180) / Math.PI : n;
  // PB Vision's camera FOV appears to behave more like a horizontal/model FOV.
  // Three's PerspectiveCamera expects vertical FOV, so this gets the default
  // much closer before the per-game calibration offsets are applied.
  const assumedSourceAspect = 4 / 3;
  const horizontalRadians = (horizontalDegrees * Math.PI) / 180;
  return (
    (2 * Math.atan(Math.tan(horizontalRadians / 2) / assumedSourceAspect) * 180) /
    Math.PI
  );
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function findCvCameraSegment(cvJson: CvJson | null | undefined, shotAbsMs: number | null) {
  const segments = (cvJson as any)?.camera?.cameraSegments;
  if (!Array.isArray(segments) || !segments.length) return null;
  if (typeof shotAbsMs === "number" && Number.isFinite(shotAbsMs)) {
    const match = segments.find((segment: any) => {
      const s = Number(segment?.s);
      const e = Number(segment?.e);
      return Number.isFinite(s) && Number.isFinite(e) && shotAbsMs >= s && shotAbsMs <= e;
    });
    if (match) return match;
  }
  return segments[0];
}

function getVideoCameraView(args: {
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  cameraAdjust?: ShotUniverseCameraAdjust;
}): CameraView {
  const segment = findCvCameraSegment(args.cvJson, args.shotAbsMs);
  const cameraSource = segment ?? (args.cvJson as any)?.camera ?? null;
  const rawPosition = cameraSource?.position;
  const position =
    rawPosition &&
    typeof rawPosition.x === "number" &&
    typeof rawPosition.y === "number"
      ? pointToThree({
          x: rawPosition.x,
          y: rawPosition.y,
          z: typeof rawPosition.z === "number" ? rawPosition.z : 6,
        })
      : new Vector3(0, 7, 48);
  const orientation = cameraSource?.orientation;
  const yaw = Number(orientation?.yaw);
  const pitch = Number(orientation?.pitch);
  const orientationTarget =
    Number.isFinite(yaw) && Number.isFinite(pitch)
      ? (() => {
          const forwardPb = {
            x: Math.cos(pitch) * Math.cos(yaw),
            y: Math.cos(pitch) * Math.sin(yaw),
            z: Math.sin(pitch),
          };
          const distance = 60;
          return new Vector3(
            position.x + forwardPb.x * distance,
            position.y + forwardPb.z * distance,
            position.z + forwardPb.y * distance,
          );
        })()
      : new Vector3(0, 0, 0);
  const adjust = args.cameraAdjust ?? {
    position: [0, 0, 0],
    target: [0, 0, 0],
    fov: 0,
    roll: 0,
    offset: [0, 0] as [number, number],
  };
  const adjustedPosition = new Vector3(
    position.x + adjust.position[0],
    position.y + adjust.position[1],
    position.z + adjust.position[2],
  );
  const adjustedTarget = new Vector3(
    orientationTarget.x + adjust.target[0],
    orientationTarget.y + adjust.target[1],
    orientationTarget.z + adjust.target[2],
  );

  return {
    position: [adjustedPosition.x, adjustedPosition.y, adjustedPosition.z],
    target: [adjustedTarget.x, adjustedTarget.y, adjustedTarget.z],
    fov: clampNumber(fovToVerticalDegrees(cameraSource?.fov) + adjust.fov, 20, 120),
    roll: Number(adjust.roll) || 0,
    offset: Array.isArray(adjust.offset)
      ? [Number(adjust.offset[0]) || 0, Number(adjust.offset[1]) || 0]
      : [0, 0],
  };
}

function getInspectionCameraView(): CameraView {
  const target = new Vector3(0, 1.5, 0);
  const offset = new Vector3(0, 28.5, 52);
  const position = target.clone().add(offset);

  return {
    position: [position.x, position.y, position.z],
    target: [target.x, target.y, target.z],
    fov: 54,
    roll: 0,
    offset: [0, 0],
  };
}

export function ShotUniverse3DView(props: {
  context: ShotUniverseContext | null;
  movementContext?: ShotUniverseContext | null;
  lingeringActualContext?: ShotUniverseContext | null;
  lingeringActualOpacity?: number;
  shotFamily: ShotUniverseShotFamily;
  showActual: boolean;
  showGhosts: boolean;
  showTargets: boolean;
  cvJson?: CvJson | null;
  shotAbsMs?: number | null;
  cameraAdjust?: ShotUniverseCameraAdjust;
  videoViewResetNonce?: number;
  rotationSteps?: number;
  transparentBackground?: boolean;
  useVideoCameraView?: boolean;
  viewBleedPx?: number;
  hideVideoAlignedCourtLines?: boolean;
  interactive?: boolean;
  interactionBlockedRef?: React.RefObject<boolean>;
  visibility?: Partial<ShotUniverseVisibility>;
  overlayPortalContainer?: HTMLElement | null;
  suggestedTarget?: { x: number; z: number } | null;
  suggestedTargetPathLabel?: string;
  focusCustomTrajectoryOnly?: boolean;
  onCameraInteraction?: () => void;
  onVideoCourtProjection?: (projection: ShotUniverseVideoCourtProjection | null) => void;
  onLabSelectionChange?: (selection: ShotUniverseLabSelection) => void;
  restoreLabSelection?: ShotUniverseLabSelectionRestore | null;
}) {
  const [selectedShapeFanOptionId, setSelectedShapeFanOptionId] =
    React.useState<string | null>(null);
  const [savedShapeFanOptionIds, setSavedShapeFanOptionIds] = React.useState<string[]>(
    [],
  );
  const [hoveredShapeFanLabel, setHoveredShapeFanLabel] = React.useState<string | null>(
    null,
  );
  const [selectedShapeFanLabel, setSelectedShapeFanLabel] = React.useState<string | null>(
    null,
  );
  const [showActualFitPanel, setShowActualFitPanel] = React.useState(true);
  const [hoveredActualFitLabel, setHoveredActualFitLabel] = React.useState<string | null>(
    null,
  );
  const [showTargetHint, setShowTargetHint] = React.useState(true);
  const [customShapeFanControl, setCustomShapeFanControl] =
    React.useState<CustomShapeFanControl>(CUSTOM_SHAPE_FAN_DEFAULT);
  const [actualFitPanelFrame, setActualFitPanelFrame] = React.useState({
    left: 10,
    bottom: 10,
  });
  const [selectedTargetPanelFrame, setSelectedTargetPanelFrame] = React.useState(
    DEFAULT_SELECTED_TARGET_PANEL_FRAME,
  );
  const onLabSelectionChange = props.onLabSelectionChange;
  const [canvasCompositeReady, setCanvasCompositeReady] = React.useState(
    !props.transparentBackground,
  );
  const canvasRevealFrameRef = React.useRef<number | null>(null);
  const actualFitPanelDragRef = React.useRef<null | {
    startX: number;
    startY: number;
    startLeft: number;
    startBottom: number;
  }>(null);
  const selectedTargetPanelDragRef = React.useRef<null | {
    startX: number;
    startY: number;
    startRight: number;
    startTop: number;
  }>(null);
  React.useEffect(() => {
    if (canvasRevealFrameRef.current != null) {
      cancelAnimationFrame(canvasRevealFrameRef.current);
      canvasRevealFrameRef.current = null;
    }
    setCanvasCompositeReady(!props.transparentBackground);
  }, [props.transparentBackground]);
  React.useEffect(
    () => () => {
      if (canvasRevealFrameRef.current != null) {
        cancelAnimationFrame(canvasRevealFrameRef.current);
        canvasRevealFrameRef.current = null;
      }
    },
    [],
  );
  const interactive = props.interactive ?? true;
  const visibility = React.useMemo(
    () => resolveVisibility(props.visibility),
    [props.visibility],
  );
  const cameraView = React.useMemo(
    () =>
      props.transparentBackground || props.useVideoCameraView
        ? getVideoCameraView({
            cvJson: props.cvJson,
            shotAbsMs: props.shotAbsMs ?? null,
            cameraAdjust: props.cameraAdjust,
          })
        : getInspectionCameraView(),
    [
      props.transparentBackground,
      props.useVideoCameraView,
      props.cvJson,
      props.shotAbsMs,
      props.cameraAdjust,
    ],
  );
  const visibleOptions = React.useMemo(
    () =>
      props.context?.options.filter(
        (option) => props.shotFamily === "all" || option.shotFamily === props.shotFamily,
      ) ?? [],
    [props.context, props.shotFamily],
  );
  const visibleTargetChoiceCount = React.useMemo(
    () => countUniqueTargetChoices(visibleOptions),
    [visibleOptions],
  );
  const selectedShapeFanOption =
    visibleOptions.find((option) => option.id === selectedShapeFanOptionId) ?? null;
  const savedShapeFanOptionIdSet = React.useMemo(
    () => new Set(savedShapeFanOptionIds),
    [savedShapeFanOptionIds],
  );
  const savedShapeFanOptions = React.useMemo(
    () =>
      savedShapeFanOptionIds
        .map((id) => visibleOptions.find((option) => option.id === id) ?? null)
        .filter((option): option is ShotUniverseOption => option != null),
    [savedShapeFanOptionIds, visibleOptions],
  );
  const shapeFanVariants = React.useMemo(
    () => buildShapeFanVariants(selectedShapeFanOption),
    [selectedShapeFanOption],
  );
  const opportunityCustomGeometry = React.useMemo<CustomShapeFanGeometry | null>(() => {
    if (!props.focusCustomTrajectoryOnly || !props.suggestedTarget || !props.context) {
      return null;
    }
    const start =
      props.context.contactPoint?.clone() ??
      props.context.actualTrajectory[0]?.clone() ??
      selectedShapeFanOption?.trajectory[0]?.clone() ??
      null;
    if (!start) return null;
    const targetPoint = selectedShapeFanOption?.target ?? props.suggestedTarget;
    const targetY = selectedShapeFanOption?.target.y ?? 0.35;
    return {
      start,
      target: new Vector3(targetPoint.x, targetY, targetPoint.z),
    };
  }, [
    props.context,
    props.focusCustomTrajectoryOnly,
    props.suggestedTarget,
    selectedShapeFanOption,
  ]);
  const customShapeFanVariant = React.useMemo(
    () =>
      buildCustomShapeFanVariant(
        selectedShapeFanOption,
        customShapeFanControl,
        opportunityCustomGeometry,
      ),
    [customShapeFanControl, opportunityCustomGeometry, selectedShapeFanOption],
  );
  React.useEffect(() => {
    if (
      selectedShapeFanLabel &&
      selectedShapeFanLabel.startsWith("Custom") &&
      customShapeFanVariant?.label !== selectedShapeFanLabel
    ) {
      setSelectedShapeFanLabel(customShapeFanVariant?.label ?? null);
    }
  }, [customShapeFanVariant?.label, selectedShapeFanLabel]);
  const actualShapeFanVariant = React.useMemo(
    () => buildActualShapeFanVariant(props.context),
    [props.context],
  );
  const visibleShapeFanVariants = React.useMemo(
    () => {
      const generated = SHOW_LOB_SHAPE_FAN_VARIANT
        ? shapeFanVariants
        : shapeFanVariants.filter((variant) => variant.label !== "Lob");
      const maxMphVariants = generated.filter((variant) =>
        variant.label.startsWith("Max mph"),
      );
      const bestDropVariants = generated.filter((variant) =>
        variant.label.startsWith("Best drop"),
      );
      const normalVariants = generated.filter(
        (variant) =>
          !variant.label.startsWith("Max mph") &&
          !variant.label.startsWith("Best drop"),
      );
      const allVariants = [
        ...(actualShapeFanVariant ? [actualShapeFanVariant] : []),
        ...maxMphVariants,
        ...bestDropVariants,
        ...(customShapeFanVariant ? [customShapeFanVariant] : []),
        ...normalVariants,
      ];
      if (!props.focusCustomTrajectoryOnly) return allVariants;
      const actualOnly = allVariants.filter((variant) => variant.isActualPath).slice(0, 1);
      const customOnly = allVariants.filter((variant) => variant.isCustomPath).slice(0, 1);
      return [...actualOnly, ...customOnly];
    },
    [
      actualShapeFanVariant,
      customShapeFanVariant,
      props.focusCustomTrajectoryOnly,
      shapeFanVariants,
    ],
  );
  const actualFitVariants = React.useMemo(
    () => (SHOW_ACTUAL_FIT_EXPERIMENT ? buildActualFitVariants(props.context) : []),
    [props.context],
  );
  const actualLandingExtrapolation = React.useMemo(
    () => buildActualShotLandingExtrapolation(props.context),
    [props.context],
  );

  const lingeringActualExtrapolation = React.useMemo(
    () => buildActualShotLandingExtrapolation(props.lingeringActualContext ?? null),
    [props.lingeringActualContext],
  );
  const selectedTargetSummary = React.useMemo(
    () => summarizeSelectedTarget(selectedShapeFanOption),
    [selectedShapeFanOption],
  );
  const happyPathHooksEnabled =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("happyPath") === "1";
  const actualFitSummary = React.useMemo(
    () => (SHOW_ACTUAL_FIT_EXPERIMENT ? summarizeActualFit(props.context) : null),
    [props.context],
  );
  const customControlConfig =
    customShapeFanControl.mode === "rpm"
      ? { min: -1800, max: 2600, step: 100, value: customShapeFanControl.rpm, suffix: "rpm" }
      : customShapeFanControl.mode === "angle"
        ? { min: 1, max: 70, step: 1, value: customShapeFanControl.angle, suffix: "deg" }
        : { min: 6, max: 95, step: 0.5, value: customShapeFanControl.mph, suffix: "mph" };
  const updateCustomControlValue = React.useCallback(
    (value: number) => {
      setSelectedShapeFanLabel("Custom");
      setHoveredShapeFanLabel(null);
      setCustomShapeFanControl((current) => {
        if (current.mode === "rpm") return { ...current, rpm: Math.round(value / 100) * 100 };
        if (current.mode === "angle") return { ...current, angle: Math.round(value) };
        return { ...current, mph: value };
      });
    },
    [],
  );
  const resetCustomControl = React.useCallback(() => {
    setCustomShapeFanControl(CUSTOM_SHAPE_FAN_DEFAULT);
    setSelectedShapeFanLabel("Custom");
    setHoveredShapeFanLabel(null);
  }, []);
  const selectShapeFanOption = React.useCallback((optionId: string) => {
    setHoveredShapeFanLabel(null);
    setSelectedShapeFanLabel(null);
    setCustomShapeFanControl(CUSTOM_SHAPE_FAN_DEFAULT);
    setSelectedShapeFanOptionId(optionId);
    setSavedShapeFanOptionIds((current) =>
      current.includes(optionId) ? current : [...current, optionId],
    );
  }, []);
  const selectShapeFanOptionAtIndex = React.useCallback(
    (index: number) => {
      if (!visibleOptions.length) return;
      const normalizedIndex =
        ((Math.round(index) % visibleOptions.length) + visibleOptions.length) %
        visibleOptions.length;
      selectShapeFanOption(visibleOptions[normalizedIndex]!.id);
      setShowTargetHint(false);
    },
    [selectShapeFanOption, visibleOptions],
  );
  const selectDistinctShapeFanOption = React.useCallback(() => {
    if (!visibleOptions.length) return;
    const savedIds = new Set(savedShapeFanOptionIds);
    const anchors =
      savedShapeFanOptions.length > 0
        ? savedShapeFanOptions
        : selectedShapeFanOption
          ? [selectedShapeFanOption]
          : [];
    const candidates = visibleOptions.filter((option) => !savedIds.has(option.id));
    const pool = candidates.length ? candidates : visibleOptions;
    let best = pool[0] ?? null;
    let bestScore = Number.NEGATIVE_INFINITY;
    for (const option of pool) {
      const score = anchors.length
        ? Math.min(
            ...anchors.map((anchor) =>
              Math.hypot(option.target.x - anchor.target.x, option.target.z - anchor.target.z),
            ),
          )
        : 0;
      if (score > bestScore) {
        best = option;
        bestScore = score;
      }
    }
    if (!best) return;
    selectShapeFanOption(best.id);
    setShowTargetHint(false);
  }, [
    savedShapeFanOptionIds,
    savedShapeFanOptions,
    selectShapeFanOption,
    selectedShapeFanOption,
    visibleOptions,
  ]);

  React.useEffect(() => {
    const primaryTarget = selectedShapeFanOption
      ? {
          x: selectedShapeFanOption.target.x,
          y: selectedShapeFanOption.target.y,
          z: selectedShapeFanOption.target.z,
          label: selectedShapeFanOption.heuristicLabel,
          pathLabel: selectedShapeFanLabel,
        }
      : null;
    const targets = savedShapeFanOptions.map((option) => ({
      x: option.target.x,
      y: option.target.y,
      z: option.target.z,
      label: option.heuristicLabel,
      pathLabel:
        selectedShapeFanOptionId === option.id ? selectedShapeFanLabel : null,
    }));
    onLabSelectionChange?.({
      target: primaryTarget,
      targets,
      pathLabel: selectedShapeFanLabel,
    });
  }, [
    onLabSelectionChange,
    savedShapeFanOptions,
    selectedShapeFanLabel,
    selectedShapeFanOption,
    selectedShapeFanOptionId,
  ]);

  React.useEffect(() => {
    setSelectedShapeFanOptionId(null);
    setSavedShapeFanOptionIds([]);
    setHoveredShapeFanLabel(null);
    setSelectedShapeFanLabel(null);
    setHoveredActualFitLabel(null);
    setShowTargetHint(true);
    setShowActualFitPanel(true);
    setCustomShapeFanControl(CUSTOM_SHAPE_FAN_DEFAULT);
    setActualFitPanelFrame({ left: 10, bottom: 10 });
    setSelectedTargetPanelFrame(DEFAULT_SELECTED_TARGET_PANEL_FRAME);
  }, [props.context?.shotNumber, props.context?.rallyIndex, props.shotFamily]);

  React.useEffect(() => {
    const target = props.suggestedTarget;
    if (!target || !visibleOptions.length) return;
    let best = visibleOptions[0] ?? null;
    let bestDistance = Infinity;
    for (const option of visibleOptions) {
      const distance = Math.hypot(option.target.x - target.x, option.target.z - target.z);
      if (distance < bestDistance) {
        best = option;
        bestDistance = distance;
      }
    }
    if (!best) return;
    setSelectedShapeFanOptionId((current) => (current === best.id ? current : best.id));
    setSavedShapeFanOptionIds((current) =>
      current.includes(best.id) ? current : [best.id, ...current],
    );
    setSelectedShapeFanLabel("Custom");
    setHoveredShapeFanLabel(null);
    setShowTargetHint(false);
    setShowActualFitPanel(true);
    setSelectedTargetPanelFrame(DEFAULT_SELECTED_TARGET_PANEL_FRAME);
  }, [
    props.suggestedTarget,
    props.suggestedTarget?.x,
    props.suggestedTarget?.z,
    visibleOptions,
  ]);

  React.useEffect(() => {
    const restore = props.restoreLabSelection;
    if (!restore?.nonce || visibleOptions.length === 0) return;
    const restoreTargets = restore.targets?.length
      ? restore.targets
      : restore.target
        ? [restore.target]
        : [];
    if (!restoreTargets.length) return;
    const matchedIds: string[] = [];
    for (const target of restoreTargets) {
      let bestOption: ShotUniverseOption | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (const option of visibleOptions) {
        const distance = Math.hypot(
          option.target.x - target.x,
          option.target.y - target.y,
          option.target.z - target.z,
        );
        if (distance < bestDistance) {
          bestDistance = distance;
          bestOption = option;
        }
      }
      if (bestOption && bestDistance <= 1.25 && !matchedIds.includes(bestOption.id)) {
        matchedIds.push(bestOption.id);
      }
    }
    if (!matchedIds.length) return;
    setSavedShapeFanOptionIds(matchedIds);
    setSelectedShapeFanOptionId(matchedIds[0] ?? null);
    setSelectedShapeFanLabel(restore.pathLabel ?? null);
    setShowTargetHint(false);
  }, [props.restoreLabSelection?.nonce, props.restoreLabSelection, visibleOptions]);

  React.useEffect(() => {
    function onMove(event: MouseEvent) {
      const actualFitDrag = actualFitPanelDragRef.current;
      if (actualFitDrag) {
        setActualFitPanelFrame({
          left: Math.max(0, actualFitDrag.startLeft + event.clientX - actualFitDrag.startX),
          bottom: Math.max(0, actualFitDrag.startBottom - (event.clientY - actualFitDrag.startY)),
        });
      }

      const selectedTargetDrag = selectedTargetPanelDragRef.current;
      if (selectedTargetDrag) {
        setSelectedTargetPanelFrame({
          right: Math.max(
            0,
            selectedTargetDrag.startRight - (event.clientX - selectedTargetDrag.startX),
          ),
          top: Math.max(
            0,
            selectedTargetDrag.startTop + event.clientY - selectedTargetDrag.startY,
          ),
        });
      }
    }

    function onUp() {
      actualFitPanelDragRef.current = null;
      selectedTargetPanelDragRef.current = null;
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const activeReferenceIndex = selectedShapeFanOptionId
    ? savedShapeFanOptions.findIndex((option) => option.id === selectedShapeFanOptionId)
    : -1;
  const activeReferenceLabel =
    activeReferenceIndex >= 0 ? referenceLabelForIndex(activeReferenceIndex) : null;
  const activeReferenceMeta = selectedShapeFanOption
    ? `${formatHeuristicLabel(selectedShapeFanOption.heuristicLabel)} target · ${formatTargetDistance(
        selectedShapeFanOption,
      )}`
    : null;

  if (!props.context) {
    if (props.transparentBackground) {
      return null;
    }
    return (
      <div style={{ color: "#ddd", padding: 12 }}>
        No Shot Target Lab data is available for this rally/shot.
      </div>
    );
  }

  const initialCanvasCameraPosition = props.transparentBackground
    ? cameraView.position
    : ([
        COURT_ORBIT_TARGET.x + COURT_ORBIT_FALLBACK_OFFSET.x,
        COURT_ORBIT_TARGET.y + COURT_ORBIT_FALLBACK_OFFSET.y,
        COURT_ORBIT_TARGET.z + COURT_ORBIT_FALLBACK_OFFSET.z,
      ] as [number, number, number]);
  const initialCanvasFov = props.transparentBackground ? cameraView.fov : COURT_ORBIT_FOV;
  const webGlFallback = <ShotUniverseWebGlFallback />;

  return (
    <div
      data-happy-path="shot-target-lab-canvas-host"
      data-target-option-count={visibleOptions.length}
      data-selected-target={selectedShapeFanOption ? "true" : "false"}
      data-selected-path={selectedShapeFanLabel ? "true" : "false"}
      data-selected-target-id={selectedShapeFanOption?.id ?? ""}
      data-saved-target-count={savedShapeFanOptions.length}
      data-target-marker-count={visibleTargetChoiceCount}
      data-canvas-composite-ready={canvasCompositeReady ? "true" : "false"}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <ShotUniverseErrorBoundary fallback={webGlFallback}>
        <Canvas
          key={props.transparentBackground ? "video-transparent-canvas" : "court-opaque-canvas"}
          dpr={[0.75, 1]}
          fallback={webGlFallback}
          style={{
            pointerEvents:
              canvasCompositeReady && interactive && !props.interactionBlockedRef?.current
                ? "auto"
                : "none",
            background: "transparent",
            opacity: props.transparentBackground && !canvasCompositeReady ? 0 : 1,
          }}
          gl={{
            antialias: false,
            powerPreference: "low-power",
            alpha: Boolean(props.transparentBackground),
            premultipliedAlpha: false,
          }}
          onCreated={({ gl }) => {
            if (props.transparentBackground) {
              gl.setClearColor("#000000", 0);
              canvasRevealFrameRef.current = requestAnimationFrame(() => {
                canvasRevealFrameRef.current = requestAnimationFrame(() => {
                  canvasRevealFrameRef.current = null;
                  setCanvasCompositeReady(true);
                });
              });
            } else {
              setCanvasCompositeReady(true);
            }
          }}
          camera={{
            position: initialCanvasCameraPosition,
            fov: initialCanvasFov,
            near: 0.1,
            far: 200,
          }}
        >
          {props.transparentBackground ? null : <color attach="background" args={["#080808"]} />}
          <ShotUniverseScene
            context={props.context}
            movementContext={props.movementContext}
            lingeringActualContext={props.lingeringActualContext}
            lingeringActualOpacity={props.lingeringActualOpacity}
            lingeringActualExtrapolation={lingeringActualExtrapolation}
            shotFamily={props.shotFamily}
            showActual={props.showActual}
            showGhosts={props.showGhosts}
            showTargets={props.showTargets}
            cameraView={cameraView}
            viewBleedPx={props.viewBleedPx}
            stableCourtOrbit={!props.transparentBackground}
            hideVideoAlignedCourtLines={props.hideVideoAlignedCourtLines}
            resetNonce={props.videoViewResetNonce ?? 0}
            rotationSteps={props.rotationSteps ?? 0}
            interactive={interactive}
            interactionBlockedRef={props.interactionBlockedRef}
            visibility={visibility}
            onCameraInteraction={props.onCameraInteraction}
            onVideoCourtProjection={props.onVideoCourtProjection}
            selectedShapeFanOptionId={selectedShapeFanOptionId}
            selectedShapeFanOptionIds={savedShapeFanOptionIdSet}
            shapeFanVariants={visibleShapeFanVariants}
            hoveredShapeFanLabel={hoveredShapeFanLabel}
            selectedShapeFanLabel={selectedShapeFanLabel}
            focusCustomTrajectoryOnly={props.focusCustomTrajectoryOnly}
            actualLandingExtrapolation={actualLandingExtrapolation}
            actualFitVariants={showActualFitPanel ? actualFitVariants : []}
            hoveredActualFitLabel={hoveredActualFitLabel}
            onSelectShapeFanOption={selectShapeFanOption}
            onHoverShapeFanVariant={setHoveredShapeFanLabel}
            onSelectShapeFanVariant={setSelectedShapeFanLabel}
            onHoverActualFitVariant={setHoveredActualFitLabel}
          />
        </Canvas>
      </ShotUniverseErrorBoundary>
      {happyPathHooksEnabled && visibleOptions[0] ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "auto",
            zIndex: 6,
          }}
        >
          <button
            type="button"
            data-happy-path="shot-target-select-random"
            tabIndex={-1}
            onClick={() => {
              selectShapeFanOptionAtIndex(Math.random() * visibleOptions.length);
            }}
          >
            Select random target
          </button>
          <button
            type="button"
            data-happy-path="shot-target-select-next"
            tabIndex={-1}
            onClick={() => {
              selectDistinctShapeFanOption();
            }}
          >
            Select another target
          </button>
          <button
            type="button"
            data-happy-path="shot-target-select-demo-comparison"
            tabIndex={-1}
            onClick={() => {
              const referenceId =
                typeof window !== "undefined"
                  ? String((window as any).__pbvDemoReferenceTargetId ?? "")
                  : "";
              const reference = visibleOptions.find((option) => option.id === referenceId) ?? null;
              const candidates = visibleOptions.filter((option) => option.id !== referenceId);
              const pool = candidates.length ? candidates : visibleOptions;
              let best = pool[0] ?? null;
              let bestScore = Number.NEGATIVE_INFINITY;
              for (const option of pool) {
                const score = reference
                  ? Math.hypot(
                      option.target.x - reference.target.x,
                      option.target.z - reference.target.z,
                    )
                  : 0;
                if (score > bestScore) {
                  best = option;
                  bestScore = score;
                }
              }
              if (!best) return;
              setHoveredShapeFanLabel(null);
              setSelectedShapeFanLabel(null);
              setCustomShapeFanControl(CUSTOM_SHAPE_FAN_DEFAULT);
              setSelectedShapeFanOptionId(best.id);
              setSavedShapeFanOptionIds(
                reference && reference.id !== best.id ? [reference.id, best.id] : [best.id],
              );
              setShowTargetHint(false);
            }}
          >
            Select demo comparison target
          </button>
          {visibleOptions.map((option) => (
            <button
              key={`happy-path-select-option-${option.id}`}
              type="button"
              data-happy-path="shot-target-select-option"
              data-target-id={option.id}
              tabIndex={-1}
              onClick={() => {
                selectShapeFanOption(option.id);
                setShowTargetHint(false);
              }}
            >
              Select {option.id}
            </button>
          ))}
          <button
            type="button"
            data-happy-path="shot-target-clear-selection"
            tabIndex={-1}
            onClick={() => {
              setHoveredShapeFanLabel(null);
              setSelectedShapeFanOptionId(null);
              setSavedShapeFanOptionIds([]);
              setSelectedShapeFanLabel(null);
              setShowTargetHint(true);
            }}
          >
            Clear target
          </button>
        </div>
      ) : null}
      {props.showTargets && showTargetHint && !selectedShapeFanOption ? (
        <div
          data-happy-path="shot-target-field-hint"
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            maxWidth: 360,
            padding: "8px 10px",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 6,
            background: "rgba(0,0,0,0.72)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.42)",
            color: "rgba(255,255,255,0.9)",
            fontFamily:
              'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 12.5,
            lineHeight: 1.25,
            pointerEvents: "auto",
            zIndex: 5,
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: PRACTICE_TARGET_ORANGE,
              boxShadow: `0 0 0 3px rgba(242,183,101,0.22)`,
              flex: "0 0 auto",
            }}
          />
          <span>
            All {visibleTargetChoiceCount} possible target choices are shown on the court.
          </span>
          <button
            type="button"
            onClick={() => setShowTargetHint(false)}
            style={{
              marginLeft: "auto",
              border: "1px solid rgba(255,255,255,0.28)",
              background: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.82)",
              borderRadius: 4,
              height: 20,
              padding: "0 7px",
              cursor: "pointer",
            }}
          >
            Hide
          </button>
        </div>
      ) : null}
      {actualFitVariants.length && actualFitSummary ? (
        showActualFitPanel ? (
          <div
            style={{
              position: "absolute",
              left: actualFitPanelFrame.left,
              bottom: actualFitPanelFrame.bottom,
              width: 520,
              maxWidth: "calc(100% - 20px)",
              padding: "12px 13px",
              border: "1px solid rgba(255,255,255,0.34)",
              borderRadius: 6,
              background: "rgba(0,0,0,0.93)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.58)",
              color: "#ffffff",
              fontFamily:
                'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: 12.5,
              lineHeight: 1.4,
              pointerEvents: "auto",
              zIndex: 5,
            }}
          >
            <div
              onMouseDown={(event) => {
                actualFitPanelDragRef.current = {
                  startX: event.clientX,
                  startY: event.clientY,
                  startLeft: actualFitPanelFrame.left,
                  startBottom: actualFitPanelFrame.bottom,
                };
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 7,
                cursor: "move",
                userSelect: "none",
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 13.5 }}>Actual shot fit</div>
              <button
                type="button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => {
                  setHoveredActualFitLabel(null);
                  setShowActualFitPanel(false);
                }}
                style={{
                  marginLeft: "auto",
                  border: "1px solid rgba(255,255,255,0.36)",
                  background: "rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.9)",
                  borderRadius: 4,
                  height: 20,
                  padding: "0 10px",
                  cursor: "pointer",
                }}
              >
                Hide
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "72px 1fr",
                gap: "2px 8px",
                padding: "7px 0 8px",
                borderTop: "1px solid rgba(255,255,255,0.16)",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              <div style={{ color: "rgba(255,255,255,0.62)" }}>distance</div>
              <div>
                {actualFitSummary.distanceFt == null
                  ? "n/a"
                  : `${actualFitSummary.distanceFt.toFixed(1)} ft`}
              </div>
            </div>
            <div
              style={{
                marginTop: 2,
                color: "rgba(255,255,255,0.72)",
                fontWeight: 850,
              }}
            >
              Physics fits
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 46px 52px 50px 52px 58px 68px",
                gap: 8,
                marginTop: 5,
                padding: "0 1px 2px",
                color: "rgba(255,255,255,0.56)",
                fontSize: 11.5,
                fontWeight: 800,
              }}
            >
              <div>fit</div>
              <div style={{ textAlign: "right" }}>angle</div>
              <div style={{ textAlign: "right" }}>spin</div>
              <div style={{ textAlign: "right" }}>launch mph</div>
              <div style={{ textAlign: "right" }}>flight</div>
              <div style={{ textAlign: "right" }}>avg mph</div>
              <div style={{ textAlign: "right" }}>RMS err ft</div>
            </div>
            {actualFitVariants.map((variant, index) => {
              const hovered = hoveredActualFitLabel === variant.label;
              const best = index === 0;
              return (
                <div
                  key={variant.label}
                  onMouseEnter={() => setHoveredActualFitLabel(variant.label)}
                  onMouseLeave={() => setHoveredActualFitLabel(null)}
                  style={{
                    margin: "3px -6px 0",
                    padding: "6px",
                    border: best
                      ? `1px solid ${variant.color}`
                      : "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 4,
                    background: hovered
                      ? "rgba(255,255,255,0.18)"
                      : best
                        ? "rgba(53,224,255,0.16)"
                        : "transparent",
                    boxShadow: best ? `inset 3px 0 0 ${variant.color}` : undefined,
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 46px 52px 50px 52px 58px 68px",
                      gap: 8,
                      alignItems: "baseline",
                    }}
                  >
                    <div style={{ color: variant.color, fontWeight: 900 }}>
                      {best ? `${variant.label} best` : variant.label}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {variant.launchAngleDeg.toFixed(0)}°
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {Math.round(variant.spinRpm / 100) / 10}k
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {variant.initialSpeedMph.toFixed(1)}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {variant.estimatedArrivalSec.toFixed(2)}s
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {variant.averageSpeedMph == null
                        ? "n/a"
                        : variant.averageSpeedMph.toFixed(1)}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {variant.fitErrorFt == null ? "n/a" : variant.fitErrorFt.toFixed(1)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowActualFitPanel(true)}
            style={{
              position: "absolute",
              left: actualFitPanelFrame.left,
              bottom: actualFitPanelFrame.bottom,
              border: "1px solid rgba(255,255,255,0.34)",
              background: "rgba(0,0,0,0.75)",
              color: "rgba(255,255,255,0.9)",
              borderRadius: 6,
              padding: "5px 10px",
              fontWeight: 800,
              cursor: "pointer",
              pointerEvents: "auto",
              zIndex: 5,
            }}
          >
            Actual fit
          </button>
        )
      ) : null}
      {selectedShapeFanOption && selectedTargetSummary ? (
        <MaybePortal container={props.overlayPortalContainer}>
        <div
          data-happy-path="shot-target-paths-panel"
          style={{
            position: "absolute",
            right: selectedTargetPanelFrame.right,
            top: selectedTargetPanelFrame.top,
            width: 500,
            maxWidth: "calc(100% - 20px)",
            maxHeight: "calc(100% - 104px)",
            overflowY: "auto",
            padding: "8px 10px 9px",
            border: "1px solid rgba(255,255,255,0.36)",
            borderRadius: 6,
            background: "rgba(0,0,0,0.95)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.62)",
            color: "#ffffff",
            fontFamily:
              'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 12.5,
            lineHeight: 1.4,
            pointerEvents: "auto",
            zIndex: props.overlayPortalContainer ? 90 : 5,
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              selectedTargetPanelDragRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                startRight: selectedTargetPanelFrame.right,
                startTop: selectedTargetPanelFrame.top,
              };
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
              cursor: "move",
              userSelect: "none",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 13.5 }}>
              {props.focusCustomTrajectoryOnly
                ? "Opportunity Paths"
                : "Possible Paths to Target"}{" "}
              <span style={{ color: "rgba(255,255,255,0.68)", fontWeight: 650 }}>
                (
                {selectedTargetSummary.distanceFt == null
                  ? "distance n/a"
                  : `distance ${selectedTargetSummary.distanceFt.toFixed(1)} ft`}
                )
              </span>
            </div>
            {props.focusCustomTrajectoryOnly && visibleOptions.length > 1 ? (
              <button
                type="button"
                data-happy-path="shot-target-add-comparison-target"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => {
                  selectDistinctShapeFanOption();
                }}
                style={{
                  marginLeft: "auto",
                  border: "1px solid rgba(53,224,255,0.55)",
                  background: "rgba(53,224,255,0.14)",
                  color: "rgba(255,255,255,0.95)",
                  borderRadius: 4,
                  height: 20,
                  padding: "0 10px",
                  cursor: "pointer",
                  fontSize: 11.5,
                  fontWeight: 850,
                }}
              >
                Add comparison
              </button>
            ) : null}
            <button
              type="button"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => {
                setHoveredShapeFanLabel(null);
                setSelectedShapeFanOptionId(null);
                setSavedShapeFanOptionIds([]);
                setShowTargetHint(true);
              }}
              style={{
                marginLeft:
                  props.focusCustomTrajectoryOnly && visibleOptions.length > 1 ? 4 : "auto",
                border: "1px solid rgba(255,255,255,0.36)",
                background: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.9)",
                borderRadius: 4,
                height: 20,
                padding: "0 10px",
                cursor: "pointer",
              }}
            >
              Clear target
            </button>
          </div>
          {savedShapeFanOptions.length > 0 ? (
            <div
              data-happy-path="shot-target-reference-list"
              style={{
                margin: "6px 0 9px",
                padding: "7px 8px 8px",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 5,
                background: "rgba(255,255,255,0.055)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div style={{ color: "rgba(255,255,255,0.86)", fontWeight: 900 }}>
                  Target references
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.52)",
                    fontSize: 10.5,
                    fontWeight: 750,
                  }}
                >
                  both stay visible
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    savedShapeFanOptions.length > 1
                      ? "repeat(2, minmax(0, 1fr))"
                      : "minmax(0, 1fr)",
                  gap: 6,
                }}
              >
                {savedShapeFanOptions.map((option, index) => {
                  const primary = option.id === selectedShapeFanOptionId;
                  const referenceLabel = referenceLabelForIndex(index);
                  const metaText = `${formatHeuristicLabel(option.heuristicLabel)} target · ${formatTargetDistance(
                    option,
                  )}`;
                  return (
                    <button
                      type="button"
                      key={`saved-target-reference-${option.id}`}
                      data-happy-path="shot-target-reference-card"
                      data-shot-target-reference-index={index + 1}
                      data-shot-target-selected={primary ? "true" : "false"}
                      onClick={() => {
                        selectShapeFanOption(option.id);
                        setShowTargetHint(false);
                      }}
                      style={{
                        width: "100%",
                        minHeight: 54,
                        padding: "6px 7px",
                        border: primary
                          ? "1px solid rgba(53,224,255,0.86)"
                          : "1px solid rgba(255,255,255,0.22)",
                        borderRadius: 5,
                        background: primary
                          ? "rgba(53,224,255,0.13)"
                          : "rgba(255,255,255,0.06)",
                        boxSizing: "border-box",
                        color: "inherit",
                        font: "inherit",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: primary ? CUSTOM_PATH_COLOR : "#75f49b",
                            boxShadow: primary
                              ? `0 0 0 2px rgba(255,139,216,0.16)`
                              : `0 0 0 2px rgba(117,244,155,0.14)`,
                            flex: "0 0 auto",
                          }}
                        />
                        <span
                          style={{
                            color: primary ? "#ffffff" : "rgba(255,255,255,0.88)",
                            fontWeight: 950,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {referenceLabel}
                        </span>
                        {primary ? (
                          <span
                            style={{
                              marginLeft: "auto",
                              border: "1px solid rgba(53,224,255,0.48)",
                              borderRadius: 999,
                              padding: "0 6px",
                              color: "rgba(255,255,255,0.88)",
                              background: "rgba(53,224,255,0.12)",
                              fontSize: 9.5,
                              fontWeight: 900,
                              whiteSpace: "nowrap",
                            }}
                          >
                            editing
                          </span>
                        ) : null}
                      </div>
                      <div
                        style={{
                          marginTop: 5,
                          color: "rgba(255,255,255,0.62)",
                          fontSize: 10.5,
                          fontWeight: 760,
                          lineHeight: 1.15,
                        }}
                      >
                        {metaText}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {visibleShapeFanVariants.map((variant) => {
            const isCustomPath = variant.isCustomPath === true;
            const pathReaction = estimatePathReaction(variant, props.context);
            const rowSelected = shapeFanLabelMatchesVariant(
              selectedShapeFanLabel,
              variant,
            );
            const rowHighlighted =
              shapeFanLabelMatchesVariant(hoveredShapeFanLabel, variant) ||
              rowSelected;
            const speedText =
              variant.averageSpeedMph == null
                ? "n/a mph"
                : `${variant.averageSpeedMph.toFixed(1)} mph`;
            const pathMetaText = variant.isActualPath
              ? `${speedText} · read only`
              : `${variant.launchAngleDeg.toFixed(0)}° · ${speedText} · ${formatSpinRpm(
                  variant.spinRpm,
                )} rpm${variant.stoppedAtNet ? " · hits net" : variant.cannotHitTarget ? " · can't hit target" : ""}`;
            return (
              <div
                key={shapeFanVariantIdentity(variant)}
                data-happy-path={
                  variant.isActualPath
                    ? "shot-target-actual-trajectory-row"
                    : isCustomPath
                      ? "shot-target-custom-trajectory-row"
                      : undefined
                }
                data-shot-target-row-kind={
                  variant.isActualPath ? "actual" : isCustomPath ? "custom" : "candidate"
                }
                onMouseEnter={() =>
                  setHoveredShapeFanLabel(variant.isActualPath ? null : variant.label)
                }
                onMouseLeave={() => setHoveredShapeFanLabel(null)}
                onClick={() => {
                  if (variant.isActualPath) return;
                  setSelectedShapeFanLabel(rowSelected ? null : variant.label);
                }}
                style={{
                  height: isCustomPath ? 84 : 48,
                  boxSizing: "border-box",
                  margin: "2px -4px 0",
                  padding: "2px 5px",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 4,
                  background:
                    rowHighlighted
                      ? "rgba(255,255,255,0.17)"
                      : variant.isActualPath
                        ? "rgba(255,255,255,0.055)"
                        : "transparent",
                  cursor: variant.isActualPath ? "default" : "pointer",
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ color: variant.color, fontWeight: 900 }}>
                    {variant.isActualPath
                      ? "Actual shot"
                      : isCustomPath
                        ? activeReferenceLabel
                          ? `${activeReferenceLabel} editable path`
                          : props.suggestedTargetPathLabel ?? "Suggested Optimal Shot (Editable)"
                        : variant.label}
                  </div>
                  <div
                    style={{
                      marginTop: 2,
                      color: variant.isActualPath
                        ? "rgba(255,255,255,0.52)"
                        : "rgba(255,255,255,0.68)",
                      fontSize: 9.5,
                      fontWeight: 800,
                      lineHeight: 1.05,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {variant.isActualPath
                      ? pathMetaText
                      : isCustomPath
                        ? `${activeReferenceMeta ? `${activeReferenceMeta} · ` : ""}${variant.label} · ${pathMetaText}`
                        : pathMetaText}
                  </div>
                </div>
                <ShapeFanVariantMiniPlot
                  variant={variant}
                  highlighted={rowHighlighted}
                  reactionText={
                    pathReaction ? `${pathReaction.timeSec.toFixed(2)}s` : "-"
                  }
                />
                {isCustomPath ? (
                  <div
                    onPointerDown={(event) => {
                      event.stopPropagation();
                    }}
                    onPointerMove={(event) => {
                      event.stopPropagation();
                    }}
                    onPointerUp={(event) => {
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onMouseMove={(event) => event.stopPropagation()}
                    onMouseUp={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      gridColumn: "1 / -1",
                      display: "grid",
                      gridTemplateColumns: "auto auto auto auto 1fr auto",
                      gap: 5,
                      alignItems: "center",
                      marginTop: -2,
                    }}
                  >
                    {(["rpm", "angle", "mph"] as const).map((mode) => (
                      <button
                        key={`custom-lock-${mode}`}
                        type="button"
                        data-happy-path={`shot-target-adjust-${mode}`}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedShapeFanLabel("Custom");
                          setHoveredShapeFanLabel(null);
                          setCustomShapeFanControl((current) => ({ ...current, mode }));
                        }}
                        style={{
                          border: "1px solid rgba(255,255,255,0.22)",
                          background:
                            customShapeFanControl.mode === mode
                              ? "rgba(255,255,255,0.18)"
                              : "rgba(255,255,255,0.06)",
                          color: "rgba(255,255,255,0.9)",
                          borderRadius: 4,
                          height: 19,
                          padding: "0 6px",
                          fontSize: 10.5,
                          fontWeight: 850,
                          cursor: "pointer",
                        }}
                      >
                        adjust {mode}
                      </button>
                    ))}
                    <button
                      type="button"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        resetCustomControl();
                      }}
                      style={{
                        border: "1px solid rgba(255,255,255,0.22)",
                        background: "rgba(255,255,255,0.06)",
                        color: "rgba(255,255,255,0.86)",
                        borderRadius: 4,
                        height: 19,
                        padding: "0 6px",
                        fontSize: 10.5,
                        fontWeight: 850,
                        cursor: "pointer",
                      }}
                    >
                      reset
                    </button>
                    <input
                      type="range"
                      data-happy-path="shot-target-custom-trajectory-slider"
                      data-custom-mode={customShapeFanControl.mode}
                      data-custom-value={String(customControlConfig.value)}
                      min={customControlConfig.min}
                      max={customControlConfig.max}
                      step={customControlConfig.step}
                      value={customControlConfig.value}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        setSelectedShapeFanLabel("Custom");
                        setHoveredShapeFanLabel(null);
                      }}
                      onPointerMove={(event) => event.stopPropagation()}
                      onPointerUp={(event) => event.stopPropagation()}
                      onInput={(event) =>
                        updateCustomControlValue(Number(event.currentTarget.value))
                      }
                      onChange={(event) => updateCustomControlValue(Number(event.target.value))}
                      style={{ width: "100%" }}
                    />
                    <div
                      style={{
                        minWidth: 56,
                        textAlign: "right",
                        color: "rgba(255,255,255,0.72)",
                        fontSize: 10.5,
                        fontWeight: 850,
                      }}
                    >
                      {customShapeFanControl.mode === "angle"
                        ? `${customControlConfig.value.toFixed(0)}°`
                        : `${customControlConfig.value.toFixed(
                            customShapeFanControl.mode === "mph" ? 1 : 0,
                          )} ${customControlConfig.suffix}`}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        </MaybePortal>
      ) : null}
    </div>
  );
}
